import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, Link, useParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import ReactHtmlParser from 'react-html-parser';
import NewHeader from './newHeader';
import Footer from './Footer';
import EventHeroBanner from './components/EventHeroBanner';
import CourseInfoSection from './components/CourseInfoSection';
import EventTabsSection from './components/EventTabsSection';
import TestimonialsSection from './components/TestimonialsSection';
import CommunityCallToAction from './components/CommunityCallToAction';
import RelatedProductsSection from './components/RelatedProductsSection';
import PastEventsGallery from './components/PastEventsGallery';
import * as myConstClass from './constant.js';
import { cartHasMagazines, cartHasEvents, getCartConflictMessage, getCartDisplayCount } from './constant.js';
import { updateGuestCartDisplay, getGuestCartConflictMessage } from './utils/guestCartHelpers';
import { getLanguage, setLanguage } from 'react-switch-lang';
import { extractStartDateFromRange, extractLocationForUrl, buildDeepLink, shareDeepLink, detectMarathiInUrl } from './utils/deepLink';
import { EventScheduleCard, RelatedEventCard, VideoTestimonialCard } from './components/eventCards';
import MachineTestWidget from './MachineTestWidget';
import ReactImageVideoLightbox from 'react-image-video-lightbox';
import calendarIcon from './assets/Garbh Samarthya/vectors-02.png';
import videoIcon from './assets/Garbh Samarthya/vectors-03.png';
import thumbImage from './assets/images/thumb.png';
import MachineTests from './assets/images/MachineTests.jpg';
import MachineTests_mr from './assets/images/Machine_Tests_m_mr.jpg';
import { translate } from 'react-switch-lang';
import $ from 'jquery';
import './eventDetailsPage.css';

// Helper: normalize & format topic name for display (handles "Prefix: Name" and "Something (Name)" patterns)
const formatTopicNameForDisplay = (raw = '') => {
  if (!raw) return '';
  let formatted = String(raw).trim();

  // Pattern: "Prefix: Name" -> return Name
  const colonPattern = /^[^:]+:\s*(.+)$/;
  const colonMatch = formatted.match(colonPattern);
  if (colonMatch) formatted = colonMatch[1].trim();

  // Pattern: "Something (Name)" -> return Name
  const parenthesesPattern = /^[^(]+\(([^)]+)\)$/;
  const parenthesesMatch = formatted.match(parenthesesPattern);
  if (parenthesesMatch) formatted = parenthesesMatch[1].trim();

  return formatted;
};

const flattenSchedules = (schedules = []) => {
  
  return schedules;
};

// Helper: extract topic info (displayName, originalName, topicId) from event object or filterOptions
const getTopicInfoForEvent = (event = {}, filterOptions = {}) => {
  const currentLanguage = getLanguage();
  const isMarathi = currentLanguage === 'ma';

  // First priority: check event.topics array (enriched from Node API response — most reliable source,
  // works for both languages without depending on filterOptions timing)
  if (Array.isArray(event.topics) && event.topics.length > 0) {
    const firstTopic = event.topics[0];
    const id = firstTopic.topicId || firstTopic.id || firstTopic.topic_id;
    const name = isMarathi
      ? (firstTopic.titleMr || firstTopic.nameMr || firstTopic.topic_title_mr || firstTopic.titleEn || firstTopic.nameEn || firstTopic.name || firstTopic.title || '')
      : (firstTopic.titleEn || firstTopic.nameEn || firstTopic.topic_title_en || firstTopic.titleMr || firstTopic.nameMr || firstTopic.name || firstTopic.title || '');
    if (id) {
      return {
        displayName: formatTopicNameForDisplay(name) || '',
        originalName: name || '',
        topicId: id
      };
    }
  }

  // Try language-aware fields on event (standalone fields — legacy/alternative API shapes)
  let topicFromEvent = '';
  if (isMarathi) {
    topicFromEvent = event.topicTitleMr || event.topicMr || event.topicTitle || event.topic || event.topicName || event.topic_title_mr || event.topic_title || event.topic_title_en || '';
  } else {
    topicFromEvent = event.topicTitleEn || event.topicTitle || event.topicEn || event.topic || event.topicName || event.topic_title_en || event.topic_title || event.topic_title_mr || '';
  }

  const topicId = event.topicId || event.topic_id || event.topicIdFk || event.topic_id_fk;

  if (topicFromEvent && topicId) {
    return {
      displayName: formatTopicNameForDisplay(topicFromEvent),
      originalName: topicFromEvent,
      topicId: topicId
    };
  }

  // Try to lookup by topicId in filterOptions
  if (topicId && filterOptions.topics && filterOptions.topics.length > 0) {
    const topic = filterOptions.topics.find(t => {
      const id = t.id || t.topic_id || t.topicId;
      return id && (id.toString() === topicId.toString() || id === topicId);
    });
    if (topic) {
      const topicName = isMarathi
        ? (topic.nameMr || topic.titleMr || topic.topic_title_mr || topic.name || topic.title || topic.topic_title || '')
        : (topic.nameEn || topic.titleEn || topic.topic_title_en || topic.name || topic.title || topic.topic_title || '');
      return {
        displayName: formatTopicNameForDisplay(topicName),
        originalName: topicName,
        topicId: topicId
      };
    }
  }

  // Check subtopic parent topic id fields
  const subtopicTopicId = event.subtopicTopicId || event.subtopic_topic_id || event.subTopicTopicId || event.sub_topic_topic_id || event.topicIdFk || event.topic_id_fk;
  if (subtopicTopicId && filterOptions.topics && filterOptions.topics.length > 0) {
    const topic = filterOptions.topics.find(t => {
      const id = t.id || t.topic_id || t.topicId;
      return id && (id.toString() === subtopicTopicId.toString() || id === subtopicTopicId);
    });
    if (topic) {
      const topicName = isMarathi
        ? (topic.nameMr || topic.titleMr || topic.topic_title_mr || topic.name || topic.title || topic.topic_title || '')
        : (topic.nameEn || topic.titleEn || topic.topic_title_en || topic.name || topic.title || topic.topic_title || '');
      return {
        displayName: formatTopicNameForDisplay(topicName),
        originalName: topicName,
        topicId: subtopicTopicId
      };
    }
  }

  return null;
};

/**
 * Transform flat schedule array from new API to component format
 * @param {Array} schedules - Flat array of schedules from API
 * @param {string} language - Current language ('en' or 'ma')
 * @returns {Array} Transformed schedules array
 */
const transformSchedulesToComponentFormat = (schedules, language) => {
  if (!Array.isArray(schedules) || schedules.length === 0) {
    return [];
  }

  // Filter only active schedules (status = '1') and future dates
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // Get today as YYYY-MM-DD string for comparison
  const todayStr = today.toISOString().split('T')[0];
  
  const activeSchedules = schedules.filter(schedule => {
    // Compare as strings to avoid timezone issues
    const isActive = schedule.status === '1';
    const isFuture = schedule.startDateOriginal >= todayStr;
    
    return isActive && isFuture;
  });

  // Transform each schedule to match component expectations
  return activeSchedules.map(schedule => ({
    schedule_id: schedule.scheduleId,
    event_id: schedule.eventId,
    trust_id: schedule.trustId,
    
    // Dates
    start_date: schedule.startDateOriginal,
    end_date: schedule.endDateOriginal,
    start_date_format: formatDisplayDate(schedule.startDate),
    end_date_format: formatDisplayDate(schedule.endDate),
    
    // Location - use language-specific fields
    city_name: language === 'ma' ? (schedule.cityNameMr || schedule.cityName) : schedule.cityName,
    venue_address: language === 'ma' ? (schedule.venueAddressMr || schedule.venueAddress) : schedule.venueAddress,
    contact_details: language === 'ma' ? (schedule.contactDetailsMr || schedule.contactDetails) : schedule.contactDetails,
    
    // Event info
    title: language === 'ma' ? (schedule.eventTitleMr || schedule.eventTitleEn) : schedule.eventTitleEn,
    
    // Pricing
    first_price: schedule.firstPrice,
    first_price_for: schedule.firstPriceFor,
    second_price: schedule.secondPrice,
    second_price_for: schedule.secondPriceFor,
    
    // Status
    is_full: parseInt(schedule.isFull || 0),
    is_featured: 0, // Not in new API, default to 0
    available_in_cart: 0, // Would need to check cart separately
    
    // Other
    url: schedule.url || '',
    // Compute duration in days (inclusive) from startDateOriginal and endDateOriginal
    // Fallback to 1 day if parsing fails or end date is missing
    duration_day: (() => {
      try {
        const startParts = (schedule.startDateOriginal || '').split('-').map(Number);
        const endParts = (schedule.endDateOriginal || schedule.startDateOriginal || '').split('-').map(Number);
        if (startParts.length === 3 && endParts.length === 3) {
          const start = new Date(startParts[0], startParts[1] - 1, startParts[2]);
          const end = new Date(endParts[0], endParts[1] - 1, endParts[2]);
          const diffDays = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
          return Math.max(1, diffDays);
        }
      } catch (e) {
        // ignore and fallback
      }
      return 1;
    })(),
    total_days: (() => {
      try {
        const startParts = (schedule.startDateOriginal || '').split('-').map(Number);
        const endParts = (schedule.endDateOriginal || schedule.startDateOriginal || '').split('-').map(Number);
        if (startParts.length === 3 && endParts.length === 3) {
          const start = new Date(startParts[0], startParts[1] - 1, startParts[2]);
          const end = new Date(endParts[0], endParts[1] - 1, endParts[2]);
          const diffDays = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
          return Math.max(1, diffDays);
        }
      } catch (e) {
        // ignore and fallback
      }
      return 1;
    })(), // Not in schedule API, comes from event info
    
    // Location details
    country_name: schedule.countryName,
    state_name: schedule.stateName,
    
    // Keep month grouping for compatibility (optional)
    monthTitle: getMonthTitle(schedule.startDateOriginal),
  }));
};

/**
 * Format date for display
 * @param {string} dateStr - Date in DD-MM-YYYY format
 * @returns {string} Formatted date string
 */
const formatDisplayDate = (dateStr) => {
  if (!dateStr) return '';
  
  const [day, month, year] = dateStr.split('-');
  const date = new Date(year, parseInt(month) - 1, day);
  
  return date.toLocaleDateString('en-US', { 
    weekday: 'short',
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
};

/**
 * Get month title from date for grouping
 * @param {string} dateStr - Date in YYYY-MM-DD format
 * @returns {string} Month title
 */
const getMonthTitle = (dateStr) => {
  if (!dateStr) return '';
  
  const date = new Date(dateStr);
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  return `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
};

/**
 * Transform images data from API to component format
 * @param {Array} images - Images array from API
 * @returns {Array} - Transformed images
 */
const transformImagesToComponentFormat = (images) => {
  if (!Array.isArray(images) || images.length === 0) {
    return [];
  }

  // Filter out not-found.png and dummy/placeholder images
  const realImages = images.filter(img => 
    img.image_url && 
    img.image_url !== 'not-found.png' && 
    !img.image_url.includes('not-found') &&
    !img.image_url.includes('placeholder') &&
    !img.image_url.includes('dummy')
  );

  // Return empty array if no real images, so gallery shows "Images will be available soon"
  if (realImages.length === 0) {
    return [];
  }

  return realImages.map((img, index) => ({
    image_id: index + 1,
    image_url: img.image_url,
    title: `Event Image ${index + 1}`,
    width: 1200,
    height: 800,
  }));
};

/**
 * Check if product is in cart (for both logged-in and guest users)
 * @param {Object} product - Product object with added_in_cart array
 * @returns {boolean} - True if product is in cart
 */
const isProductInCart = (product) => {
  // Check if logged-in user has this product in cart (from API)
  if (product.added_in_cart && Array.isArray(product.added_in_cart)) {
    const hasInCart = product.added_in_cart.some(cart => cart.cart_id > 0);
    if (hasInCart) return true;
  }
  
  // Check if guest user has this product in cart (from localStorage)
  try {
    const guestCart = JSON.parse(localStorage.getItem('guest_cart_products') || '[]');
    const inGuestCart = guestCart.some(item => item.product_id == product.product_id);
    if (inGuestCart) return true;
  } catch (error) {
  }
  
  return false;
};

/**
 * Transform products data from API to component format
 * @param {Array} products - Products array from API
 * @returns {Array} - Transformed products
 */
const transformProductsToComponentFormat = (products) => {
  if (!Array.isArray(products) || products.length === 0) {
    return [];
  }

  return products.map(product => {
    const inCart = isProductInCart(product);
    
    return {
      product_id: product.product_id,
      name: product.short_title || product.title,
      description: 'Donation Contribution',
      price: product.price,
      price_label: 'INR',
      image: product.image_name || '',
      title_url: product.title_url || '',
      total_review: product.total_review || 0,
      total_like: product.total_like || 0,
      added_in_cart: product.added_in_cart || [],
      isInCart: inCart,
    };
  });
};

/**
 * Helper function to parse Draft.js JSON to plain text
 * @param {string|object} draftJsContent - Draft.js content (string or object)
 * @returns {string} Plain text extracted from Draft.js
 */
const parseDraftJsToText = (draftJsContent) => {
  if (!draftJsContent) return '';
  
  // If it's already a plain string and doesn't look like JSON, return as-is
  if (typeof draftJsContent === 'string' && !draftJsContent.trim().startsWith('{') && !draftJsContent.trim().startsWith('[')) {
    return draftJsContent;
  }
  
  let parsed;
  
  // Check if it's a string that might be JSON
  if (typeof draftJsContent === 'string') {
    try {
      parsed = JSON.parse(draftJsContent);
    } catch (e) {
      // Not valid JSON, return as-is
      return draftJsContent;
    }
  } else if (typeof draftJsContent === 'object') {
    parsed = draftJsContent;
  } else {
    return String(draftJsContent);
  }
  
  // Check if it has the Draft.js structure (blocks array)
  if (parsed && parsed.blocks && Array.isArray(parsed.blocks)) {
    // Extract text from all blocks
    const textBlocks = parsed.blocks
      .map(block => {
        // Handle different block types
        if (block.text) {
          // For list items, add appropriate formatting
          if (block.type === 'unordered-list-item') {
            return '• ' + block.text;
          }
          if (block.type === 'ordered-list-item') {
            return block.text; // You could add numbering here if needed
          }
          return block.text;
        }
        return '';
      })
      .filter(text => text && text.trim() !== '');
    
    // Join with newlines to preserve paragraph breaks
    return textBlocks.join('\n\n');
  }
  
  // If it doesn't have the expected structure, try to stringify it
  return typeof parsed === 'object' ? JSON.stringify(parsed) : String(parsed);
};

/**
 * Create a short plain-text snippet from HTML/Draft.js content
 * @param {string|object} content - content which may be HTML, plain text or Draft.js JSON
 * @param {number} maxLen - maximum length of snippet
 * @returns {string} plain-text truncated snippet
 */
const getSnippetFromContent = (content, maxLen = 140) => {
  if (!content) return '';

  // Parse possible Draft.js or JSON content into plain text
  let text = '';
  try {
    text = parseDraftJsToText(content);
  } catch (e) {
    text = typeof content === 'string' ? content : JSON.stringify(content);
  }

  // Normalize and strip HTML tags
  const stripped = String(text).replace(/<[^>]*>/g, '');
  const normalized = stripped.replace(/\s+/g, ' ').trim();

  if (normalized.length <= maxLen) return normalized;
  // If there's a full stop (first sentence end), return up to that first full stop
  const firstFullStop = normalized.indexOf('.');
  if (firstFullStop !== -1) {
    // include the full stop
    const sentence = normalized.slice(0, firstFullStop + 1).trim();
    if (sentence.length > 0) return sentence;
  }

  // No full stop found - try to truncate at the end of the last full sentence before maxLen
  const sentenceEnders = /[\.\!\?]/g;
  let lastSentenceIndex = -1;
  let match;
  while ((match = sentenceEnders.exec(normalized)) !== null) {
    if (match.index <= maxLen - 1) {
      lastSentenceIndex = match.index;
    } else {
      break;
    }
  }

  if (lastSentenceIndex > -1) {
    const endPos = lastSentenceIndex + 1;
    const candidate = normalized.slice(0, endPos).trim();
    if (candidate.length > 0) return candidate;
  }

  // If no sentence end found within limit, avoid cutting mid-word: cut at last space
  const lastSpace = normalized.lastIndexOf(' ', maxLen - 1);
  if (lastSpace > 0) {
    return normalized.slice(0, lastSpace).trim() + '…';
  }

  // Fallback: hard trim
  return normalized.slice(0, maxLen - 1).trim() + '…';
};

/**
 * Transform video data from new API to component format
 * @param {Object} video - Video object from API
 * @returns {Object} Transformed video object
 */
const transformVideoData = (video) => {
  const isYouTube = video.youtubeUrl && video.youtubeUrl !== '';
  const thumbnail = isYouTube ? video.youtubeUrl : (video.src || video.url);
  const playUrl = video.url || video.videoUrl || video.src;
  
  // Parse title from Draft.js JSON format if needed
  const rawTitle = video.imageName || video.altTag || 'Event Video';
  const parsedTitle = parseDraftJsToText(rawTitle);
  
  return {
    video_id: video.eventImageId,
    event_id: video.eventId,
    thumbnail: thumbnail,
    title: parsedTitle || 'Event Video',
    play_url: playUrl,
    is_youtube: isYouTube,
    sequence: video.sequence,
    type: video.type,
  };
};

/**
 * Extract YouTube video ID from URL
 * @param {string} url - YouTube URL in various formats
 * @returns {string|null} YouTube video ID or null
 */
const extractYouTubeVideoId = (url) => {
  if (!url) return null;
  
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/.*[?&]v=([^&\n?#]+)/
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  return null;
};

/**
 * Get YouTube embed URL from video URL
 * @param {string} videoUrl - YouTube URL in various formats
 * @returns {string} YouTube embed URL
 */
const getYouTubeEmbedUrl = (videoUrl) => {
  const videoId = extractYouTubeVideoId(videoUrl);
  return videoId ? `https://www.youtube.com/embed/${videoId}` : videoUrl;
};

/**
 * Get YouTube thumbnail URL from video URL
 * @param {string} videoUrl - YouTube URL
 * @returns {string} Thumbnail URL
 */
const getYouTubeThumbnail = (videoUrl) => {
  const videoId = extractYouTubeVideoId(videoUrl);
  return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : '';
};

/**
 * Transform testimonial data from API to component format
 * @param {Object} testimonial - Testimonial object from API
 * @returns {Object} Transformed testimonial object
 */
const transformTestimonialData = (testimonial) => {
  const videoUrl = testimonial.videoUrl || '';
  const thumbnail = testimonial.videoThumbnail || testimonial.thumbnail || getYouTubeThumbnail(videoUrl);
  // Prefer embedUrl from new API (/testimonials/event-playlist/videos) if present
  const playUrl = testimonial.embedUrl || getYouTubeEmbedUrl(videoUrl);
  
  return {
    video_id: testimonial.testimonialId,
    event_id: testimonial.eventId,
    thumbnail: thumbnail,
    title: testimonial.participantName || testimonial.title || '',
    description: testimonial.description || '',
    play_url: playUrl,
    video_url: videoUrl,
    sequence: testimonial.sequence || 0,
    createdOn: testimonial.createdOn,
  };
};
const getEventCategoryColor = (text = '') => {
  const sourceText = text.toLowerCase();

  if (sourceText.includes('newly wed') || sourceText.includes('newlywed')) {
    return 'blue';
  }

  if (
    (sourceText.includes('birth') &&
      (sourceText.includes('upto') || sourceText.includes('up to') || sourceText.includes('7'))) ||
    (
      (sourceText.includes('8') || sourceText.includes('eight')) &&
      (sourceText.includes('14') || sourceText.includes('fourteen') || sourceText.includes('to 14') || sourceText.includes('child development'))
    )
  ) {
    return 'pink';
  }

  if (
    (sourceText.includes('life') && sourceText.includes('after') && sourceText.includes('death')) ||
    (sourceText.includes('stress') && sourceText.includes('management')) ||
    (sourceText.includes('meditation') && sourceText.includes('yoga'))
  ) {
    return 'yellow';
  }

  if (
    (sourceText.includes('science') && sourceText.includes('spirituality')) ||
    (sourceText.includes('health') && sourceText.includes('care'))
  ) {
    return 'purple';
  }

  if (
    (sourceText.includes('15') || sourceText.includes('fifteen')) &&
    (sourceText.includes('28') || sourceText.includes('twenty-eight') || sourceText.includes('twenty eight'))
  ) {
    return 'green';
  }

  return 'blue';
};

const getLanguageLabel = (languageId) => {
  if (languageId == 2) return 'Marathi';
  if (languageId == 1) return 'English';
  return 'English';
};


const EventDetailsPage = (props) => {
  const location = useLocation();
  const calendarState = location.state;
  const passedTopicTitle = location.state?.topicTitle || null;
  // Read topic_id from location.state (fast-path) or ?topic_id= query param (refresh / deep-link)
  const passedTopicId = useMemo(() => {
    if (location.state?.topicId) return String(location.state.topicId);
    try {
      const urlParams = new URLSearchParams(location.search || '');
      return urlParams.get('topic_id') || null;
    } catch (e) {
      return null;
    }
  }, [location.state, location.search]);
  
  const history = useHistory();
  const handleBackToCalendar = () => {
    if (calendarState?.from === 'calendar') {
      history.push({
        pathname: '/events-calendar',
        state: calendarState
      });
    } else {
      history.push('/events-calendar');
    }
  };
  const params = useParams();

  const [eventInfo, setEventInfo] = useState(null);
  const [eventWhatsappLink, setEventWhatsappLink] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [videos, setVideos] = useState([]);
  const [videoTestimonials, setVideoTestimonials] = useState([]);
  const [loadingTestimonials, setLoadingTestimonials] = useState(false);
  const [relatedEvents, setRelatedEvents] = useState([]);
  const [loadingRelatedEvents, setLoadingRelatedEvents] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [reviewsPagination, setReviewsPagination] = useState(null);
  // Like / Dislike state
  const [likes, setLikes] = useState(0);
  const [dislikes, setDislikes] = useState(0);
  const [userReaction, setUserReaction] = useState(null); // 'like' | 'dislike' | null
  const [likeLoading, setLikeLoading] = useState(false);

  const [products, setProducts] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDots, setLoadingDots] = useState('');

  useEffect(() => {
    if (!loading || eventInfo) {
      setLoadingDots('');
      return;
    }
    let mounted = true;
    let i = 0;
    const interval = setInterval(() => {
      i = (i + 1) % 4; // 0..3
      if (mounted) setLoadingDots('.'.repeat(i));
    }, 400);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [loading, eventInfo]);
  const [whyAttendItems, setWhyAttendItems] = useState([]);
  const [loadingWhyAttend, setLoadingWhyAttend] = useState(true);
  const [venueItems, setVenueItems] = useState([]);
  const [loadingVenues, setLoadingVenues] = useState(true);
  const [stayFacilities, setStayFacilities] = useState([]);
  const [loadingStayFacilities, setLoadingStayFacilities] = useState(true);
  const [faqItems, setFaqItems] = useState([]);
  const [loadingFaqs, setLoadingFaqs] = useState(true);
  const [filterOptions, setFilterOptions] = useState({ topics: [] });
  const [loadingFilterOptions, setLoadingFilterOptions] = useState(false);
  const [instructionItems, setInstructionItems] = useState([]);
  const [loadingInstructions, setLoadingInstructions] = useState(true);
  const [topicItems, setTopicItems] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(true);
  const [scheduleDescItems, setScheduleDescItems] = useState([]);
  const [loadingScheduleDesc, setLoadingScheduleDesc] = useState(true);
  // Event subtopics fetched from Node API (now stores objects with id, name, etc.)
  const [eventSubtopics, setEventSubtopics] = useState([]);
  const [loadingSubtopics, setLoadingSubtopics] = useState(false);
  const scheduleGridRef = React.useRef(null);
  const [selectedScheduleId, setSelectedScheduleId] = useState(null);
  const [urlLanguage, setUrlLanguage] = useState(null);
  const [missingScheduleId, setMissingScheduleId] = useState(null);
  
  // Video lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxStartIndex, setLightboxStartIndex] = useState(0);
  const [lightboxVideoData, setLightboxVideoData] = useState([]);

  // Machine test modal state
  const [tempCartData, setTempCartData] = useState(null);
  
  // Track which machine tests are in cart (for showing ADD MORE vs ADD TO CART)
  const [machineTestsInCart, setMachineTestsInCart] = useState({});

  // Track if deep link has been processed in this render cycle (resets on page refresh)
  const deepLinkProcessedRef = React.useRef(false);
  // Prevent concurrent processing of the same deep-link across rapid re-renders
  const deepLinkProcessingRef = React.useRef(false);

  // Optional language override from URL (e.g. ?lang=ma)
  // Read `lang` query param once on mount or when location.search changes
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search || '');
      const lang = params.get('lang');
      if (lang) {
        setUrlLanguage(lang);
      }
    } catch (e) {
      // ignore malformed URL
    }
  }, [location.search]);

  // Initialize language from localStorage (user's explicit choice via toggle)
  // This takes priority over URL-based detection
  useEffect(() => {
    const storedLanguage = localStorage.getItem('key');
    if (storedLanguage) {
      setLanguage(storedLanguage);
      setUrlLanguage(storedLanguage);
    } else {
      // Fallback: Auto-detect language from URL content only if no explicit choice exists
      try {
        const currentUrl = window.location.href;
        const currentLanguage = getLanguage();
        const hasMarathi = detectMarathiInUrl(currentUrl);
        
        // If URL contains Marathi characters, switch to Marathi
        if (hasMarathi && currentLanguage !== 'ma') {
          setLanguage('ma');
          setUrlLanguage('ma');
          localStorage.setItem('key', 'ma');
        }
        // If URL is in English (no Marathi characters), switch to English
        else if (!hasMarathi && currentLanguage !== 'en') {
          setLanguage('en');
          setUrlLanguage('en');
          localStorage.setItem('key', 'en');
        }
      } catch (e) {
      }
    }
  }, [location.pathname, location.search]);
  const currentLanguage = getLanguage();
  const eventId = useMemo(() => {
    try {
      const params = new URLSearchParams(location.search || '');
      const fromQuery = params.get('event_id') || params.get('eventId') || params.get('id');
      if (fromQuery) return fromQuery;
    } catch (e) {
      // ignore malformed search
    }
    const parts = location.pathname.split('/');
    return parts[2] || '';
  }, [location.pathname, location.search]);

   // =======================
// LIKE / DISLIKE FETCH
// =======================
useEffect(() => {
  if (!eventId) return;

  const userId = localStorage.getItem('user_id');
  const query =
    userId && userId !== '0'
      ? `?user_id=${userId}`
      : '';

  fetch(`${myConstClass.NODE_API_BASE}/event-like-dislike/${eventId}${query}`)
    .then(res => res.json())
    .then(res => {
      if (res.status === 'true') {
        setLikes(res.data.likes || 0);
        setDislikes(res.data.dislikes || 0);
        setUserReaction(res.data.userReaction || null);
      }
    })
    .catch(() => {
    });
}, [eventId]);

  useEffect(() => {
    if (!eventId) {
      toast.error('Event not found');
      return;
    }



    const language = getLanguage();
    const userId = localStorage.getItem('user_id') || 0;
    setLoading(true);
    setLoadingSubtopics(true);

    // New getEventInfo API - Node.js API that returns event, videos, images, topics, and subtopics
    // Pass language parameter to get localized event data
    const eventInfoApiUrl = `${myConstClass.NODE_API_BASE}/event/getEventInfo/${eventId}?language=${language}`;
    

    // New getEventSchedule API is a Node.js API, use NODE_API_BASE
    const scheduleApiUrl = `${myConstClass.NODE_API_BASE}/event/getEventSchedule?event_id=${eventId}&language=${language}`;
    

    // Prepare additional Node API endpoints for videos and images
    // Use videoPlaylist/videos to fetch all resolved videos (playlists + specific) for the event
    const videoApiUrl = `${myConstClass.NODE_API_BASE}/event/media/videoPlaylist/videos?eventId=${eventId}`;
    const imagesApiUrl = `${myConstClass.NODE_API_BASE}/event/media/getImages/${eventId}/${language}`;

    // Create abort controllers for timeout handling (include video/images)
    const eventInfoController = new AbortController();
    const scheduleController = new AbortController();
    const videoController = new AbortController();
    const imagesController = new AbortController();
    const productsController = new AbortController();

    const eventInfoTimeout = setTimeout(() => eventInfoController.abort(), 5000);
    const scheduleTimeout = setTimeout(() => scheduleController.abort(), 5000);
    const videoTimeout = setTimeout(() => videoController.abort(), 5000);
    const imagesTimeout = setTimeout(() => imagesController.abort(), 5000);
    const productsTimeout = setTimeout(() => productsController.abort(), 5000);

    Promise.all([
      fetch(eventInfoApiUrl, { signal: eventInfoController.signal }).then((res) => res.json()).catch(() => {
        return { status: 'false', data: null };
      }),
      fetch(scheduleApiUrl, { signal: scheduleController.signal }).then((res) => res.json()).catch(() => {
        return { status: 'false', data: [] };
      }),
      // Direct Node.js video API
      fetch(videoApiUrl, { signal: videoController.signal }).then((res) => res.json()).catch(() => {
        return [];
      }),
      // Direct Node.js images API
      fetch(imagesApiUrl, { signal: imagesController.signal }).then((res) => res.json()).catch(() => {
        return [];
      }),
      fetch(`${myConstClass.BASE_URL}product/getRelatedProductsForEvent/${eventId}/${language}/${userId}`, { signal: productsController.signal }).then((res) => res.json()).catch(() => {
        return [];
      })
    ])
      .then(([eventInfoResponse, scheduleResponse, videoResponse, imagesResponse, productList]) => {


        // Extract event data from new API response structure
        const eventData = eventInfoResponse?.status === 'true' && eventInfoResponse?.data?.event 
          ? eventInfoResponse.data.event 
          : null;
        
        // Map the new API field names to match what the component expects
        const mappedEventInfo = eventData ? {
          ...eventData,
          // Map new field names to old field names for backward compatibility
          event_id: eventData.eventId,
          title: language === 'ma' ? (eventData.titleMr || eventData.titleEn) : eventData.titleEn,
          title_mr: eventData.titleMr,
          event_type_id: eventData.eventTypeIdFk,
          eventTypeId: eventData.eventTypeIdFk,
          trust_id_fk: eventData.trustIdFk,
          age_group: eventData.ageGroup,
          min_age: eventData.minAge,
          max_age: eventData.maxAge,
          first_price: eventData.firstPrice,
          first_price_for: eventData.firstPriceFor,
          second_price: eventData.secondPrice,
          second_price_for: eventData.secondPriceFor,
          event_type: language === 'ma' ? (eventData.eventTypeMr || eventData.eventType) : eventData.eventType,
          trust_name: eventData.trustName,
          // Use HTML versions if available, otherwise fall back to plain text
          about_course: language === 'ma' 
            ? (eventData.descriptionMrHtml || eventData.descriptionMr || eventData.descriptionHtml || eventData.description)
            : (eventData.descriptionHtml || eventData.description),
          description: language === 'ma' 
            ? (eventData.introductionMrHtml || eventData.introductionMr || eventData.introductionHtml || eventData.introduction)
            : (eventData.introductionHtml || eventData.introduction),
          short_description: language === 'ma' 
            ? (eventData.introductionMr || eventData.introduction)
            : eventData.introduction,
          created_on: eventData.createdOn,
          // Extract topic_id from eventData.topics or fallback to eventInfoResponse.data.topics
          topic_id: (() => {
            const extractId = (t) => {
              if (!t) return null;
              if (typeof t === 'object') return t.topicId || t.id || t.topic_id || null;
              return t; // already a primitive (number/string)
            };
            if (eventData.topics && Array.isArray(eventData.topics) && eventData.topics.length > 0) {
              return extractId(eventData.topics[0]);
            }
            // fallback: check if topics exist at the root of data
            const rootTopics = eventInfoResponse?.data?.topics;
            if (Array.isArray(rootTopics) && rootTopics.length > 0) {
              return extractId(rootTopics[0]);
            }
            return null;
          })()
        } : null;
        
        // Prefer direct Node API responses for videos/images; fall back to eventInfo payload
        // New API returns { success: true, data: [...], count: N }
        let videoData = [];
        if (Array.isArray(videoResponse)) {
          videoData = videoResponse;
        } else if (videoResponse && videoResponse.success === true && Array.isArray(videoResponse.data)) {
          videoData = videoResponse.data;
        } else if (videoResponse && videoResponse.status === 'true' && Array.isArray(videoResponse.data)) {
          videoData = videoResponse.data;
        } else {
          videoData = eventInfoResponse?.data?.videos || [];
        }

        let imageData = [];
        if (Array.isArray(imagesResponse)) {
          imageData = imagesResponse;
        } else if (imagesResponse && imagesResponse.status === 'true' && Array.isArray(imagesResponse.data)) {
          imageData = imagesResponse.data;
        } else {
          imageData = eventInfoResponse?.data?.images || [];
        }
        const topicData = eventInfoResponse?.data?.topics || [];
        const subtopicsArray = eventInfoResponse?.data?.subtopics || [];
        
        // Subtopics debug logs removed
        
        // Add topics and subtopics directly to mappedEventInfo (backend now returns full objects)
        if (mappedEventInfo) {
          if (Array.isArray(topicData) && topicData.length > 0) {
            mappedEventInfo.topics = topicData;
          }

          if (Array.isArray(subtopicsArray) && subtopicsArray.length > 0) {
            mappedEventInfo.subtopics = subtopicsArray;
          }

          // Update eventInfo state with enriched mappedEventInfo
          setEventInfo({ ...mappedEventInfo });
          // --- SEO: update meta tags and JSON-LD for crawlers ---
          try {
            const siteName = window.location.hostname || '';
            const pageUrl = window.location.origin + `/event_details/${mappedEventInfo.event_id}/${(mappedEventInfo.title || '').toString().toLowerCase().trim().replace(/[^a-z0-9\s-]/g,'').replace(/\s+/g,'-').replace(/-+/g,'-').replace(/^-+|-+$/g,'')}`;

            // Document title
            if (mappedEventInfo.title) {
              document.title = `${mappedEventInfo.title} | ${siteName}`;
            }

            // Meta description
            const descriptionContent = mappedEventInfo.short_description || mappedEventInfo.description || '';
            let metaDesc = document.querySelector('meta[name="description"]');
            if (!metaDesc) {
              metaDesc = document.createElement('meta');
              metaDesc.setAttribute('name', 'description');
              document.head.appendChild(metaDesc);
            }
            metaDesc.setAttribute('content', descriptionContent);

            // Open Graph tags
            const setMeta = (prop, value) => {
              let el = document.querySelector(`meta[property="${prop}"]`);
              if (!el) {
                el = document.createElement('meta');
                el.setAttribute('property', prop);
                document.head.appendChild(el);
              }
              el.setAttribute('content', value || '');
            };

            setMeta('og:title', mappedEventInfo.title || 'Event');
            setMeta('og:description', descriptionContent);
            setMeta('og:url', pageUrl);
            if (mappedEventInfo.image || (galleryImages && galleryImages[0])) {
              const img = mappedEventInfo.image || (galleryImages[0] && galleryImages[0].image_url) || '';
              setMeta('og:image', img);
            }

            // Canonical link - always point to main event page (without schedule params)
            let canonical = document.querySelector('link[rel="canonical"]');
            if (!canonical) {
              canonical = document.createElement('link');
              canonical.setAttribute('rel', 'canonical');
              document.head.appendChild(canonical);
            }
            canonical.setAttribute('href', pageUrl);
            
            // Add noindex for schedule-specific URLs (prevent duplicate content in search results)
            const isScheduleUrl = window.location.pathname.split('/').filter(Boolean).length >= 5;
            let robotsMeta = document.querySelector('meta[name="robots"]');
            if (isScheduleUrl) {
              if (!robotsMeta) {
                robotsMeta = document.createElement('meta');
                robotsMeta.setAttribute('name', 'robots');
                document.head.appendChild(robotsMeta);
              }
              robotsMeta.setAttribute('content', 'noindex, follow');
            } else if (robotsMeta) {
              // Remove noindex if it's the main event page
              robotsMeta.remove();
            }

            // JSON-LD (schema.org/Event)
            const ld = {
              '@context': 'https://schema.org',
              '@type': 'Event',
              name: mappedEventInfo.title || '',
              description: descriptionContent || '',
              url: pageUrl,
              startDate: mappedEventInfo.start_date || mappedEventInfo.start_date_format || '',
              location: {
                '@type': 'Place',
                name: mappedEventInfo.venue_address || mappedEventInfo.city_name || ''
              }
            };

            let ldScript = document.getElementById('event-jsonld');
            if (!ldScript) {
              ldScript = document.createElement('script');
              ldScript.id = 'event-jsonld';
              ldScript.type = 'application/ld+json';
              document.head.appendChild(ldScript);
            }
            ldScript.text = JSON.stringify(ld);
          } catch (e) {
          }
        }
        
        // Normalize and set videos from new API
        const normalizedVideos = Array.isArray(videoData) ? videoData : [];
        setVideos(normalizedVideos);

        // Transform and set images from new API
        const normalizedImages = Array.isArray(imageData) ? imageData : [];
        const transformedImages = transformImagesToComponentFormat(normalizedImages);
        setGalleryImages(transformedImages);
        
        // Normalize subtopics from new API - store full objects with id and name for clickable links
        try {
          if (Array.isArray(subtopicsArray) && subtopicsArray.length > 0) {
            const subtopicObjects = subtopicsArray
              .map(st => {
                const name = language === 'ma' 
                  ? (st.titleMr || st.title || st.titleEn || st.name || st.subtopic_name)
                  : (st.titleEn || st.title || st.name || st.subtopic_name);
                const id = st.id || st.subtopicId || st.subtopic_id;
                return (name && id) ? { id, name: String(name).trim() } : null;
              })
              .filter(obj => obj !== null);
            setEventSubtopics(subtopicObjects);
          } else {
            setEventSubtopics([]);
          }
        } catch (e) {
          setEventSubtopics([]);
        }
        
        // Extract and transform schedules from new API response
        const scheduleData = scheduleResponse?.status === 'true' && Array.isArray(scheduleResponse.data) 
          ? scheduleResponse.data 
          : [];
        
        
        
        const transformedSchedules = transformSchedulesToComponentFormat(scheduleData, urlLanguage || language);
        
        // Sort upcoming schedules by start date (earliest upcoming first)
        
        if (Array.isArray(transformedSchedules) && transformedSchedules.length > 1) {
          transformedSchedules.sort((a, b) => {
            const da = a.start_date || '';
            const db = b.start_date || '';
            if (!da && !db) return 0;
            if (!da) return 1;
            if (!db) return -1;
            return da.localeCompare(db);
          });
        }
        
        // Determine selected schedule (prefer URL param, then navigation state, then shared URL)
        let selectedScheduleIdFromState = params?.scheduleId || location.state?.selectedScheduleId;

        // If not provided via navigation state, attempt to parse shared URL for schedule identification
        if (!selectedScheduleIdFromState && typeof window !== 'undefined') {
          try {
            const pathname = window.location.pathname || '';
            const urlParams = new URLSearchParams(window.location.search || '');
            const pathParts = pathname.split('/').filter(Boolean);

            // Priority 1: Check query params for schedule_id (new deep link format)
            const queryScheduleId = urlParams.get('schedule_id');
            if (queryScheduleId) {
              selectedScheduleIdFromState = queryScheduleId;
            }
            // Priority 2: Use shared helpers to extract date/location slugs from query params
            else if (urlParams.get('date') || urlParams.get('location')) {
              const sharedDate = urlParams.get('date');
              const sharedLocation = urlParams.get('location');
              for (const s of transformedSchedules) {
                const scheduleDateRange = `${s.start_date_format} - ${s.end_date_format}`;
                const scheduleLocation = `${s.city_name || ''}${s.venue_address ? `, ${s.venue_address}` : ''}`;
                const sd = extractStartDateFromRange(scheduleDateRange);
                const sl = extractLocationForUrl(scheduleLocation);
                const dateMatches = sharedDate ? sd === sharedDate : false;
                const locationMatches = sharedLocation ? sl === sharedLocation : false;
                if (dateMatches || locationMatches) {
                  selectedScheduleIdFromState = s.schedule_id;
                  break;
                }
              }
            }
            // Priority 3: If route contains explicit schedule id segment: /.../schedule/{id}/...
            else {
              const scheduleIdx = pathParts.indexOf('schedule');
              if (scheduleIdx !== -1 && pathParts[scheduleIdx + 1]) {
                selectedScheduleIdFromState = pathParts[scheduleIdx + 1];
              } else if (pathParts.length >= 5 && pathParts[0] === 'event_details') {
                // Expected path: /event_details/{event_id}/{slug}/{date}/{location}
                const pathDate = pathParts[3] || '';
                const pathLocation = pathParts[4] || '';

                // Try to find a converted schedule that matches date+location
                for (const s of transformedSchedules) {
                  const scheduleDateRange = `${s.start_date_format} - ${s.end_date_format}`;
                  const scheduleLocation = `${s.city_name || ''}${s.venue_address ? `, ${s.venue_address}` : ''}`;
                  const sd = extractStartDateFromRange(scheduleDateRange);
                  const sl = extractLocationForUrl(scheduleLocation);
                  if (sd === pathDate && sl === pathLocation) {
                    selectedScheduleIdFromState = s.schedule_id;
                    break;
                  }
                }
              }
            }
          } catch (e) {
          }
        }

        // If URL specified a schedule id, ensure it exists in transformed schedules
        if (selectedScheduleIdFromState) {
          const exists = transformedSchedules.some(s => String(s.schedule_id) === String(selectedScheduleIdFromState));
          if (exists) {
            setSelectedScheduleId(selectedScheduleIdFromState);
          } else {
            // Graceful handling: requested schedule not available (expired/removed)
            // Don't show toast for machine tests as they don't have "Upcoming Events" section
            const isMachineTest = mappedEventInfo && (mappedEventInfo.event_type_id == 3 || (mappedEventInfo.event_type || '').toLowerCase().includes('machine'));
            if (!isMachineTest) {
              setMissingScheduleId(selectedScheduleIdFromState);
              toast.warn('Requested schedule is not available. Showing event overview.');
            }
            // do not set selectedScheduleId
            selectedScheduleIdFromState = null;
          }
        }

        // Sort schedules to show selected one first
        const sortedSchedules = selectedScheduleIdFromState
          ? [
              ...transformedSchedules.filter(s => s.schedule_id == selectedScheduleIdFromState),
              ...transformedSchedules.filter(s => s.schedule_id != selectedScheduleIdFromState)
            ]
          : transformedSchedules;

        setSchedules(sortedSchedules);
        
        // Handle products response
        
        
        const normalizedProducts = Array.isArray(productList) ? productList : [];
        const transformedProducts = transformProductsToComponentFormat(normalizedProducts);
        
        setProducts(transformedProducts);
      })
      .catch((error) => {
        toast.error('Unable to load event details. Please try again later.');
      })
      .finally(() => {
        clearTimeout(eventInfoTimeout);
        clearTimeout(scheduleTimeout);
        clearTimeout(productsTimeout);
        setLoading(false);
        setLoadingSubtopics(false);
      });
  }, [eventId]); // Only re-fetch when event changes, not when language changes

  

  // Handle admin deep-link for immediate checkout: ?intent=checkout&schedule_id=...
  useEffect(() => {
    (async () => {
      try {
        const params = new URLSearchParams(location.search || '');
        const intent = params.get('intent');

        if (!intent || intent.toLowerCase() !== 'checkout') {
          return;
        }

        // Allow explicit machine-test flag in URL to be processed even if eventInfo
        // hasn't loaded yet. Example: ?intent=checkout&schedule_id=123&is_machine=1
        const isMachineParam = (params.get('is_machine') === '1') || (params.get('is_machine') === 'true') || ((params.get('event_type') || '').toLowerCase() === 'machine');

        // Check if already processed or currently being processed
        if (deepLinkProcessedRef.current || deepLinkProcessingRef.current) {
          return;
        }

        const scheduleIdParam = params.get('schedule_id') || params.get('scheduleId');

        // If schedules haven't loaded and no schedule_id provided, nothing to match against
        if ((!schedules || schedules.length === 0) && !scheduleIdParam) {
          return;
        }

        // If eventInfo is absent and no explicit machine flag, try fetching minimal event info
        let effectiveEventInfo = eventInfo;
        if (!effectiveEventInfo && !isMachineParam) {
          try {
            const currentLang = getLanguage();
            const infoResp = await fetch(`${myConstClass.NODE_API_BASE}/event/getEventInfo/${eventId}?language=${currentLang}`);
            const infoJson = await infoResp.json();
            if (infoJson?.status === 'true' && infoJson?.data?.event) {
              // Normalize commonly used fields from the new API shape so detection works
              const raw = infoJson.data.event;
              effectiveEventInfo = {
                ...raw,
                // numeric event type id may come as eventTypeIdFk or eventTypeId
                event_type_id: raw.eventTypeIdFk || raw.eventTypeId || raw.event_type_id || raw.eventTypeIdFk || null,
                // textual event type may be in different fields
                event_type: raw.eventTypeMr || raw.eventType || raw.event_type || (raw.eventTypeEn || '') || '',
                // pricing fallbacks
                first_price: raw.firstPrice || raw.first_price || raw.firstprice || 0,
                second_price: raw.secondPrice || raw.second_price || raw.secondprice || 0,
                firstPrice: raw.firstPrice || raw.first_price || raw.firstprice || 0,
                secondPrice: raw.secondPrice || raw.second_price || raw.secondprice || 0,
                title: raw.titleEn || raw.title || raw.title_en || '',
                title_mr: raw.titleMr || raw.title_mr || ''
              };
            }
          } catch (err) {
            // ignore — we'll fallback where possible
          }
        }

        // Find schedule in loaded schedules if available
        let targetSchedule = null;
        if (scheduleIdParam && schedules && schedules.length > 0) {
          targetSchedule = schedules.find(s => String(s.schedule_id) === String(scheduleIdParam));
        }

        // If schedule not found but schedule_id provided, create a minimal schedule object
        if (!targetSchedule && scheduleIdParam) {
          targetSchedule = {
            schedule_id: scheduleIdParam,
            title: effectiveEventInfo?.title || 'Machine Test',
            first_price: effectiveEventInfo?.firstPrice || effectiveEventInfo?.first_price || '0',
            child_price: effectiveEventInfo?.secondPrice || effectiveEventInfo?.second_price || '0',
            first_price_for: effectiveEventInfo?.firstPriceFor || effectiveEventInfo?.first_price_for || 'Person',
            second_price_for: effectiveEventInfo?.secondPriceFor || effectiveEventInfo?.second_price_for || '',
            start_date: '',
            end_date: '',
            city_name: '',
            venue_address: ''
          };
        }

        // If we have only schedule_id (from deep link) but missing date/location info,
        // try to fetch schedule details from the API so cart shows correct date and venue.
        if (targetSchedule && scheduleIdParam && (!targetSchedule.start_date || !targetSchedule.city_name || !targetSchedule.venue_address)) {
          try {
            const currentLang = getLanguage();
            const schedResp = await fetch(`${myConstClass.NODE_API_BASE}/event/getEventSchedule?event_id=${eventId}&language=${currentLang}`);
            const schedJson = await schedResp.json();
            if (schedJson && schedJson.status === 'true' && Array.isArray(schedJson.data)) {
              const found = schedJson.data.find(s => String(s.scheduleId) === String(scheduleIdParam) || String(s.schedule_id) === String(scheduleIdParam));
              if (found) {
                // Populate commonly used fields
                targetSchedule.start_date = found.startDateOriginal || found.startDate || targetSchedule.start_date || '';
                targetSchedule.end_date = found.endDateOriginal || found.endDate || targetSchedule.end_date || '';
                targetSchedule.city_name = found.cityName || found.city_name || '';
                targetSchedule.venue_address = found.venueAddress || found.venue_address || '';
                // Ensure title/price fallbacks
                targetSchedule.title = targetSchedule.title || (found.eventTitleEn || found.eventTitle || '');
                targetSchedule.first_price = targetSchedule.first_price || found.firstPrice || found.first_price || targetSchedule.first_price;
                targetSchedule.child_price = targetSchedule.child_price || found.secondPrice || found.second_price || targetSchedule.child_price;
              }
            }
          } catch (e) {
            // ignore fetch errors - we'll still store what we have
          }
        }

        if (!targetSchedule) {
          // no matching schedule available
          toast.warn('Requested schedule is not available.');
          deepLinkProcessedRef.current = true;
          return;
        }

        // Build booking object similar to handleBookNow
        let topic_id = null;
        if (effectiveEventInfo?.topicId) {
          topic_id = effectiveEventInfo.topicId;
        } else if (Array.isArray(effectiveEventInfo?.topics) && effectiveEventInfo.topics.length > 0) {
          topic_id = effectiveEventInfo.topics[0].id || effectiveEventInfo.topics[0].topic_id || null;
        }

        const eventBookingData = {
          event_id: eventId,
          schedule_id: targetSchedule.schedule_id,
          parent: 0,
          child: 0,
          title: targetSchedule.title || effectiveEventInfo?.title || 'Event Booking',
          title_mr: effectiveEventInfo?.title_mr || targetSchedule.title || effectiveEventInfo?.title || 'Event Booking',
          price: targetSchedule.first_price || effectiveEventInfo?.first_price || '0',
          child_price: targetSchedule.second_price || effectiveEventInfo?.second_price || effectiveEventInfo?.first_price || '0',
          first_price_for: targetSchedule.first_price_for || effectiveEventInfo?.first_price_for || 'Person',
          second_price_for: targetSchedule.second_price_for || effectiveEventInfo?.second_price_for || '',
          event_type: effectiveEventInfo?.event_type || 'Event',
          event_type_id: effectiveEventInfo?.event_type_id || effectiveEventInfo?.eventTypeId || 1,
          image: effectiveEventInfo?.image_name || effectiveEventInfo?.image || '',
          start_date: targetSchedule.start_date || targetSchedule.startDateOriginal || '',
          end_date: targetSchedule.end_date || targetSchedule.endDateOriginal || '',
          venue: targetSchedule.city_name || '',
          venue_address: targetSchedule.venue_address || '',
          selected_at: new Date().toISOString(),
          topic_id: topic_id
        };

        const user_id = localStorage.getItem('user_id');

        // final decision: explicit URL param OR eventInfo-derived
        const eventTypeIdCandidate = effectiveEventInfo?.event_type_id || effectiveEventInfo?.eventTypeIdFk || effectiveEventInfo?.eventTypeId || null;
        const eventTypeStrCandidate = (effectiveEventInfo?.event_type || effectiveEventInfo?.eventType || effectiveEventInfo?.eventTypeMr || '').toString().toLowerCase();
        const isMachineTest = isMachineParam || (Number(eventTypeIdCandidate) === 3) || (eventTypeStrCandidate.includes('machine'));
        if (isMachineTest) {
          // Acquire processing lock to avoid concurrent add attempts
          deepLinkProcessingRef.current = true;
          try {
            // Guest user: store in localStorage
            if (!user_id || user_id === '0') {
              // Ensure sensible participant defaults for guest deep-link adds
              const parentVal = (eventBookingData.parent && Number(eventBookingData.parent) > 0) ? Number(eventBookingData.parent) : 1;
              const childVal = (eventBookingData.child && Number(eventBookingData.child) > 0) ? Number(eventBookingData.child) : 0;

              // Ensure price fields fall back to effectiveEventInfo when missing
              const priceVal = (eventBookingData.price && Number(eventBookingData.price) > 0) ? eventBookingData.price : (effectiveEventInfo?.first_price || effectiveEventInfo?.firstPrice || '0');
              const childPriceVal = (eventBookingData.child_price && Number(eventBookingData.child_price) > 0) ? eventBookingData.child_price : (effectiveEventInfo?.second_price || effectiveEventInfo?.secondPrice || priceVal);

              const item = {
                event_id: eventBookingData.event_id,
                schedule_id: eventBookingData.schedule_id || targetSchedule?.schedule_id || null,
                parent: parentVal,
                child: childVal,
                title: eventBookingData.title || targetSchedule?.title || effectiveEventInfo?.title || 'Machine Test',
                price: priceVal,
                first_price: priceVal,
                second_price: childPriceVal,
                child_price: childPriceVal,
                first_price_for: eventBookingData.first_price_for || targetSchedule?.first_price_for || effectiveEventInfo?.first_price_for || 'Person',
                event_type: eventBookingData.event_type || effectiveEventInfo?.event_type || 'Machine Test',
                event_type_id: eventBookingData.event_type_id || effectiveEventInfo?.event_type_id || 3,
                venue_address: eventBookingData.venue_address || targetSchedule?.venue_address || '',
                added_at: new Date().toISOString()
              };

              try {
                const guestMtCart = JSON.parse(localStorage.getItem('guest_mt_cart') || '[]');
                const existingIndex = guestMtCart.findIndex(g => String(g.event_id) === String(item.event_id) && String(g.schedule_id) === String(item.schedule_id));
                if (existingIndex !== -1) {
                  guestMtCart[existingIndex] = item;
                } else {
                  guestMtCart.push(item);
                }
                localStorage.setItem('guest_mt_cart', JSON.stringify(guestMtCart));
                toast.success('Added to cart. Login to persist.', { containerId: 'B' });
                // Update in-memory status
                try { setMachineTestsInCart(prev => ({ ...prev, [eventId]: true })); } catch (e) { /* ignore */ }
                deepLinkProcessedRef.current = true;
                history.replace('/mycart');
                return;
              } catch (err) {
                toast.error('Error saving to cart', { containerId: 'B' });
                deepLinkProcessedRef.current = true;
                return;
              }
            }

            // Logged-in user: call backend API to add machine test to user's cart
            // Ensure sensible defaults so backend computes price correctly
            const parent = (eventBookingData.parent && Number(eventBookingData.parent) > 0) ? Number(eventBookingData.parent) : 1;
            const child = (eventBookingData.child && Number(eventBookingData.child) > 0) ? Number(eventBookingData.child) : 0;
            const url = myConstClass.BASE_URL + `cart/addMachineTestEventToCart/${eventId}/${user_id}/${parent}/${child}`;
            const resp = await fetch(url, { method: 'GET' });
            const respJson = await resp.json();
              if (respJson && respJson.status === 'true') {
              toast.success('Added to cart', { containerId: 'B' });
              // Update in-memory status so UI shows "ADD MORE"
              try {
                setMachineTestsInCart(prev => ({ ...prev, [eventId]: true }));
              } catch (e) {/* ignore */}

              // Refresh header cart count (best-effort)
              try {
                const count = await getCartDisplayCount(user_id);
                document.querySelectorAll('.lblCartCount').forEach(el => { el.textContent = count; });
                localStorage.setItem('cached_cart_count', String(count));
              } catch (e) {
              }

              deepLinkProcessedRef.current = true;
              history.replace('/mycart');
              return;
            } else {
              toast.error((respJson && respJson.message) ? respJson.message : 'Failed to add to cart', { containerId: 'B' });
              deepLinkProcessedRef.current = true;
              return;
            }
          } catch (err) {
            toast.error('Error saving to cart', { containerId: 'B' });
            deepLinkProcessedRef.current = true;
            return;
          } finally {
            // Release processing lock (component may unmount after navigation)
            deepLinkProcessingRef.current = false;
          }
        }

        // For normal events, replace booking_event and navigate to cart
        const storageKey = (user_id && user_id > 0) ? 'booking_event' : 'booking_event_guest';
        
        try {
          localStorage.setItem(storageKey, JSON.stringify(eventBookingData));
          
          // Verify the data was written successfully
          const verifyData = localStorage.getItem(storageKey);
          if (!verifyData) {
            toast.error('Failed to save booking data');
            return;
          }

          // Mark as processed in this render cycle
          deepLinkProcessedRef.current = true;

          // Add small delay to ensure all writes complete, then navigate
          setTimeout(() => {
            history.replace('/eventDetails');
          }, 150);
        } catch (storageError) {
          toast.error('Failed to save booking data');
        }
      } catch (e) {
        toast.error('Failed to process deep link');
      }
    })();
  }, [schedules, location.search, eventInfo, eventId, history]);

  // Fetch related events based on event type ID
  useEffect(() => {
    const fetchRelatedEvents = async () => {
      if (!eventInfo) {
        return;
      }


      // Use topic ID for related events
      const topicId = eventInfo.topic_id || eventInfo.topicId;
      if (!topicId) {
        return;
      }
      const language = getLanguage();
      setLoadingRelatedEvents(true);
      try {
        const params = new URLSearchParams({
          topicIds: String(topicId),
          page: '1',
          pageSize: '100',
          language: language === 'en' ? 'en' : 'ma',
          status: '1'
        });
        const apiUrl = `${myConstClass.NODE_API_BASE}/event/filter?${params.toString()}`;
        

        const response = await fetch(apiUrl);
        const data = await response.json();

        

        if (data.status === 'true' && data.data) {
          // Filter out the current event and transform to RelatedEventCard format
          const formatDate = (dateStr) => {
            if (!dateStr) return '';
            try {
              // Handle YYYY-MM-DD or DD-MM-YYYY format
              let date;
              if (dateStr.includes('-')) {
                const parts = dateStr.split('-');
                if (parts[0].length === 4) {
                  // YYYY-MM-DD format
                  date = new Date(dateStr);
                } else {
                  // DD-MM-YYYY format
                  const [day, month, year] = parts;
                  date = new Date(year, parseInt(month) - 1, day);
                }
              } else {
                date = new Date(dateStr);
              }
              
              if (isNaN(date.getTime())) return '';
              const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              return `${months[date.getMonth()]} ${date.getDate()}`;
            } catch (e) {
              return '';
            }
          };

          // Helper to fetch schedule with timeout
          const fetchScheduleWithTimeout = async (relatedEventId, timeoutMs = 3000) => {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
            
            try {
              const scheduleResponse = await fetch(
                `${myConstClass.NODE_API_BASE}/event/getEventSchedule?event_id=${relatedEventId}`,
                { signal: controller.signal }
              );
              clearTimeout(timeoutId);
              const scheduleData = await scheduleResponse.json();
              
              if (scheduleData.status === 'true' && scheduleData.data && scheduleData.data.length > 0) {
                // Get the first active schedule
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const todayStr = today.toISOString().split('T')[0];
                
                const activeSchedule = scheduleData.data.find(s => 
                  s.status === '1' && s.startDateOriginal >= todayStr
                ) || scheduleData.data[0];
                
                if (activeSchedule) {
                  return {
                    date: formatDate(activeSchedule.startDateOriginal || activeSchedule.startDate),
                    location: activeSchedule.cityName || ''
                  };
                }
              }
            } catch (error) {
              if (error.name !== 'AbortError') {
              }
            } finally {
              clearTimeout(timeoutId);
            }
            
            return { date: '', location: '' };
          };

          // Filter and prepare events for schedule fetching
          const filteredEvents = data.data
            .filter(event => {
              const currentEventId = parseInt(eventId);
              const relatedEventId = event.eventId || event.event_id;
              return relatedEventId !== currentEventId;
            });

          // Transform events immediately without schedule data
          const eventsWithoutSchedule = filteredEvents.map(event => {
            const relatedEventId = event.eventId || event.event_id || event.id;
            return {
              eventId: relatedEventId,
              title: event.title || event.titleEn || '',
              date: '',
              location: 'Lonavala',
              language: language === 'ma' ? 'Marathi' : 'English',
              description: event.description || event.descriptionEn || '',
              eventType: event.eventType || event.eventTypeEn || '',
              ageGroup: event.ageGroup || ''
            };
          });

          // Set events immediately to show UI
          setRelatedEvents(eventsWithoutSchedule);

          // Fetch schedules in background and update
          Promise.all(
            filteredEvents.map(async (event, index) => {
              const relatedEventId = event.eventId || event.event_id || event.id;
              const scheduleInfo = await fetchScheduleWithTimeout(relatedEventId);
              
              return {
                ...eventsWithoutSchedule[index],
                date: scheduleInfo.date,
                location: scheduleInfo.location || 'Lonavala'
              };
            })
          ).then(transformedEvents => {
            
            setRelatedEvents(transformedEvents);
          }).catch(() => {
            // Keep the events without schedule data if update fails
          });

        } else {
          setRelatedEvents([]);
        }
      } catch (error) {
        setRelatedEvents([]);
      } finally {
        setLoadingRelatedEvents(false);
      }
    };

    fetchRelatedEvents();
  }, [eventInfo, eventId, urlLanguage, currentLanguage]);

  // Fetch reviews for the event
  const fetchReviews = React.useCallback(async () => {
    if (!eventId) {
      return;
    }

    setLoadingReviews(true);

    try {
      const params = new URLSearchParams({
        page: '1',
        pageSize: '50' // Get up to 50 reviews (max allowed)
      });

      const apiUrl = `${myConstClass.NODE_API_BASE}/reviews/event/${eventId}?${params.toString()}`;

      // Add timeout to prevent blocking
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(apiUrl, { signal: controller.signal });
      clearTimeout(timeoutId);
      const data = await response.json();

      if (data.status === 'true' && data.data) {
        // Transform API response to match WrittenReviewCard format
        // Filter out reviews with empty or null text
        const transformedReviews = data.data
          .filter(review => review.description && review.description.trim() !== '')
          .map(review => ({
            reviewId: review.reviewId,
            text: review.description || '',
            author: review.name || 'Anonymous',
            location: review.address || '',
            createdOn: review.createdOn,
            userId: review.userId
          }));

        setReviews(transformedReviews);
        setReviewsPagination(data.pagination || null);
      } else {
        setReviews([]);
        setReviewsPagination(null);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
      }
      setReviews([]);
    } finally {
      setLoadingReviews(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  // Fetch event-level WhatsApp link from Node API
  useEffect(() => {
    const fetchWhatsappLink = async () => {
      if (!eventId) return;
      const id = parseInt(eventId, 10);
      if (Number.isNaN(id)) return;

      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        const apiUrl = `${myConstClass.NODE_API_BASE}/event/getEventWhatsappLink/${id}`;
        const res = await fetch(apiUrl, { signal: controller.signal });
        clearTimeout(timeoutId);
        const body = await res.json();

        if (body && body.status === 'true' && body.data && body.data.event_whatsapp_link) {
          setEventWhatsappLink(body.data.event_whatsapp_link);
        } else {
          setEventWhatsappLink(null);
        }
      } catch (error) {
        setEventWhatsappLink(null);
      }
    };

    fetchWhatsappLink();
  }, [eventId]);

  // Fetch video testimonials for the event
  useEffect(() => {
    const fetchVideoTestimonials = async () => {
      if (!eventId) {
        return;
      }

      setLoadingTestimonials(true);

      try {
        // New API: returns all resolved testimonial videos (playlists + specific) for the event
        // Response: { success: true, data: [...], count: N }
        const apiUrl = `${myConstClass.NODE_API_BASE}/testimonials/event-playlist/videos?eventId=${eventId}`;

        // Add timeout to prevent blocking
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const response = await fetch(apiUrl, { signal: controller.signal });
        clearTimeout(timeoutId);
        const data = await response.json();

        if (data.success && Array.isArray(data.data)) {
          // Transform testimonials to match TestimonialsSection format
          const transformedTestimonials = data.data.map(testimonial => 
            transformTestimonialData(testimonial)
          );

          setVideoTestimonials(transformedTestimonials);
        } else {
          setVideoTestimonials([]);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
        }
        setVideoTestimonials([]);
      } finally {
        setLoadingTestimonials(false);
      }
    };

    fetchVideoTestimonials();
  }, [eventId]);

  // Reset all tab data states when eventId changes (handles SPA navigation between events)
  useEffect(() => {
    setWhyAttendItems([]);
    setLoadingWhyAttend(true);
    setVenueItems([]);
    setLoadingVenues(true);
    setStayFacilities([]);
    setLoadingStayFacilities(true);
    setFaqItems([]);
    setLoadingFaqs(true);
    setInstructionItems([]);
    setLoadingInstructions(true);
    setTopicItems([]);
    setLoadingTopics(true);
    setScheduleDescItems([]);
    setLoadingScheduleDesc(true);
    setRelatedEvents([]);
    setLoadingRelatedEvents(true);
  }, [eventId]);

  // Fetch why attend items for the event
  useEffect(() => {
    if (!eventId) {
      setWhyAttendItems([]);
      setLoadingWhyAttend(false);
      return;
    }

    const controller = new AbortController();
    setLoadingWhyAttend(true);

    const language = getLanguage();
    const languageCode = language === 'en' ? 'en' : 'ma';
    const apiUrl = `${myConstClass.NODE_API_BASE}/why-attend/event/${eventId}/${languageCode}`;

    fetch(apiUrl, { signal: controller.signal })
      .then(res => res.json())
      .then(data => {
        if (data.success && Array.isArray(data.data)) {
          const sortedItems = [...data.data].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
          setWhyAttendItems(sortedItems);
        } else {
          setWhyAttendItems([]);
        }
      })
      .catch(error => {
        if (error.name !== 'AbortError') {
          setWhyAttendItems([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoadingWhyAttend(false);
        }
      });

    return () => controller.abort();
  }, [eventId]);

  // Fetch venue items for the event
  useEffect(() => {
    if (!eventId) {
      setVenueItems([]);
      setLoadingVenues(false);
      return;
    }

    const controller = new AbortController();
    setLoadingVenues(true);

    // Always use explicit URL language override first, otherwise use current app language
    const language = urlLanguage || currentLanguage || 'en';
    const languageCode = language === 'ma' ? 'ma' : 'en';
    const apiUrl = `${myConstClass.NODE_API_BASE}/venue/event/${eventId}/${languageCode}`;

    fetch(apiUrl, { signal: controller.signal })
      .then(res => res.json())
      .then(data => {
        if (data.success && Array.isArray(data.data)) {
          const sortedItems = [...data.data].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
          setVenueItems(sortedItems);
        } else {
          setVenueItems([]);
        }
      })
      .catch(error => {
        if (error.name !== 'AbortError') {
          setVenueItems([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoadingVenues(false);
        }
      });

    return () => controller.abort();
  }, [eventId, urlLanguage, currentLanguage]);

  // Fetch stay facilities for the event
  useEffect(() => {
    if (!eventId) {
      setStayFacilities([]);
      setLoadingStayFacilities(false);
      return;
    }

    const controller = new AbortController();
    setLoadingStayFacilities(true);

    const fetchStayFacilities = async () => {
      try {
        // Step 1: Get facility IDs for the event
        const mappingsApiUrl = `${myConstClass.NODE_API_BASE}/stay-facility/event/${eventId}`;
        const mappingsResponse = await fetch(mappingsApiUrl, { signal: controller.signal });
        const mappings = await mappingsResponse.json();

        if (!Array.isArray(mappings) || mappings.length === 0) {
          setStayFacilities([]);
          return;
        }

        // Step 2: Get full details for each facility
        const facilitiesPromises = mappings.map(async (mapping) => {
          try {
            const facilityApiUrl = `${myConstClass.NODE_API_BASE}/stay-facility/${mapping.stayFacilityIdFk}`;
            const facilityResponse = await fetch(facilityApiUrl, { signal: controller.signal });
            const facilityArray = await facilityResponse.json();
            return Array.isArray(facilityArray) && facilityArray.length > 0 ? facilityArray[0] : null;
          } catch (error) {
            return null;
          }
        });

        const facilities = await Promise.all(facilitiesPromises);

        // Step 3: Filter active, non-deleted facilities
        const activeFacilities = facilities.filter(
          (facility) =>
            facility !== null &&
            facility.isDeleted === '0' &&
            facility.status === '1'
        );

        setStayFacilities(activeFacilities);
      } catch (error) {
        if (error.name !== 'AbortError') {
          setStayFacilities([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingStayFacilities(false);
        }
      }
    };

    fetchStayFacilities();
    return () => controller.abort();
  }, [eventId]);

  // Fetch FAQs filtered by event_id
  useEffect(() => {
    if (!eventId) {
      setFaqItems([]);
      setLoadingFaqs(false);
      return;
    }

    const controller = new AbortController();
    setLoadingFaqs(true);

    // Fetch FAQs filtered by event_id - both English and Marathi are returned;
    // language selection happens in formatFAQsAsHTML via getLanguage()
    const faqParams = new URLSearchParams({
      event_id: eventId,
      is_deleted: '0',
      page: '1',
      pageSize: '100'
    });
    const apiUrl = `${myConstClass.NODE_API_BASE}/faq/admin/all?${faqParams.toString()}`;

    fetch(apiUrl, { signal: controller.signal })
      .then(res => res.json())
      .then(data => {
        // Handle multiple response shapes from the admin API
        let faqs = [];
        if (Array.isArray(data)) {
          faqs = data;
        } else if (data.success && Array.isArray(data.data)) {
          faqs = data.data;
        } else if (data.data && Array.isArray(data.data)) {
          faqs = data.data;
        } else if (data.faqs && Array.isArray(data.faqs)) {
          faqs = data.faqs;
        }

        // Normalize Marathi fields so formatFAQsAsHTML always finds them
        faqs = faqs.map(faq => ({
          ...faq,
          titleMr: faq.titleMr || faq.title_mr || faq.questionMr || faq.question_mr || '',
          descriptionMr: faq.descriptionMr || faq.description_mr || faq.answerMr || faq.answer_mr || '',
        }));

        setFaqItems(faqs);
      })
      .catch(error => {
        if (error.name !== 'AbortError') {
          setFaqItems([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoadingFaqs(false);
        }
      });

    return () => controller.abort();
  }, [eventId]); // Re-fetch when event changes

  // Fetch filter options (for topics)
  useEffect(() => {
    const fetchFilterOptions = async () => {
      setLoadingFilterOptions(true);

      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);
        const apiUrl = `${myConstClass.NODE_API_BASE}/event/filterOptions`;

        const response = await fetch(apiUrl, { signal: controller.signal });
        clearTimeout(timeoutId);
        const data = await response.json();

        if (data.status === 'true' && data.data) {
          setFilterOptions({
            topics: data.data.topics || [],
            eventTypes: data.data.eventTypes || []
          });
        } else {
          setFilterOptions({ topics: [], eventTypes: [] });
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
        }
        setFilterOptions({ topics: [], eventTypes: [] });
      } finally {
        setLoadingFilterOptions(false);
      }
    };

    fetchFilterOptions();
  }, []); // Fetch filter options once on mount

  // Fetch event instructions
  useEffect(() => {
    if (!eventId) {
      setInstructionItems([]);
      setLoadingInstructions(false);
      return;
    }

    const controller = new AbortController();
    setLoadingInstructions(true);

    const apiUrl = `${myConstClass.NODE_API_BASE}/event-instructions/event/${eventId}`;

    fetch(apiUrl, { signal: controller.signal })
      .then(res => res.json())
      .then(data => {
        if (data.success && Array.isArray(data.data)) {
          setInstructionItems(data.data);
        } else {
          setInstructionItems([]);
        }
      })
      .catch(error => {
        if (error.name !== 'AbortError') {
          setInstructionItems([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoadingInstructions(false);
        }
      });

    return () => controller.abort();
  }, [eventId]);

  // Fetch event topics
  useEffect(() => {
    if (!eventId) {
      setTopicItems([]);
      setLoadingTopics(false);
      return;
    }

    const controller = new AbortController();
    setLoadingTopics(true);

    const apiUrl = `${myConstClass.NODE_API_BASE}/event-topics/event/${eventId}`;

    fetch(apiUrl, { signal: controller.signal })
      .then(res => res.json())
      .then(data => {
        if (data.success && Array.isArray(data.data)) {
          setTopicItems(data.data);
        } else {
          setTopicItems([]);
        }
      })
      .catch(error => {
        if (error.name !== 'AbortError') {
          setTopicItems([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoadingTopics(false);
        }
      });

    return () => controller.abort();
  }, [eventId]);

  // Fetch event schedule descriptions
  useEffect(() => {
    if (!eventId) {
      setScheduleDescItems([]);
      setLoadingScheduleDesc(false);
      return;
    }

    const controller = new AbortController();
    setLoadingScheduleDesc(true);

    const apiUrl = `${myConstClass.NODE_API_BASE}/event-schedules/event/${eventId}`;

    fetch(apiUrl, { signal: controller.signal })
      .then(res => res.json())
      .then(data => {
        if (data.success && Array.isArray(data.data)) {
          setScheduleDescItems(data.data);
        } else {
          setScheduleDescItems([]);
        }
      })
      .catch(error => {
        if (error.name !== 'AbortError') {
          setScheduleDescItems([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoadingScheduleDesc(false);
        }
      });

    return () => controller.abort();
  }, [eventId]);

  // Transform why attend items to HTML format for display
  const formatWhyAttendAsHTML = (items) => {
    if (!Array.isArray(items) || items.length === 0) {
      return '<p>Content will be available soon.</p>';
    }

    // Check if text contains HTML tags
    const containsHTML = (text) => {
      if (!text) return false;
      return /<[a-z][\s\S]*>/i.test(text);
    };

    // Format reason text - handle both HTML and plain text
    const formatReasonText = (text) => {
      if (!text) return '';
      if (containsHTML(text)) {
        return text;
      }
      // plain text: convert newlines to <br>
      return text.replace(/\n/g, '<br>');
    };

    const itemsHTML = items.map((item, index) => {
          const reason = item.reason || '';
          if (!reason) return '';
          const formattedReason = formatReasonText(reason);

          return `
            <div style="margin-bottom: 24px; padding-bottom: 16px; border-bottom: ${index < items.length - 1 ? '1px solid #e0e0e0' : 'none'};">
              <div style="font-size: 0.875rem;">
                ${formattedReason}
              </div>
            </div>
          `;
    }).filter(html => html.trim() !== '').join('');

    return `<div style="margin: 16px 0;">${itemsHTML}</div>`;
  };

  // Transform venue items to HTML format for display
  const formatVenuesAsHTML = (venues) => {
    if (!Array.isArray(venues) || venues.length === 0) {
      return '<p>Content will be available soon.</p>';
    }



    // Check if text contains HTML tags
    const containsHTML = (text) => {
      if (!text) return false;
      return /<[a-z][\s\S]*>/i.test(text);
    };

    // Format instructions text - handle both HTML and plain text
    const formatInstructions = (instructions) => {
      if (!instructions) return '';
      if (containsHTML(instructions)) {
        return instructions;
      }
      // plain text: convert newlines to <br>
      return instructions.replace(/\n/g, '<br>');
    };

    // Create HTML for each venue
    const venuesHTML = venues.map((venue, index) => {
      const venueName = venue.venueName || '';
      const venueAddress = venue.venueAddress || '';
      const instructions = formatInstructions(venue.instructions || '');

      return `
        <div style="margin-bottom: 32px;">
          ${venueName ? `<h3 style="color: #2c2c2c; margin: 0 0 12px 0; font-size: 1.2rem; font-weight: 600;">${venueName}</h3>` : ''}
          ${venueAddress ? `<p style="color: #4a4a4a; margin: 0 0 16px 0; line-height: 1.7; font-weight: 500;"><strong>Address:</strong> ${venueAddress}</p>` : ''}
          ${instructions ? `
            <div style="margin-top: 16px;">
              <h4 style="color: #2c2c2c; margin: 0 0 12px 0; font-size: 1rem; font-weight: 600;">How to Reach:</h4>
              <div style="color: #4a4a4a; line-height: 1.7; white-space: pre-wrap;">${instructions}</div>
            </div>
          ` : ''}
        </div>
      `;
    }).join('');

    return `<div style="margin: 16px 0;">${venuesHTML}</div>`;
  };

  // Transform stay facilities to HTML format for display
  const formatStayFacilitiesAsHTML = (facilities) => {
    if (!Array.isArray(facilities) || facilities.length === 0) {
      return '<p>Content will be available soon.</p>';
    }

    const language = getLanguage();



    // Check if text contains HTML tags
    const containsHTML = (text) => {
      if (!text) return false;
      return /<[a-z][\s\S]*>/i.test(text);
    };

    // Format details text - handle both HTML and plain text
    const formatDetails = (details) => {
      if (!details) return '';
      // If text already contains HTML, use it as-is (ReactHtmlParser will handle it)
      if (containsHTML(details)) {
        return details;
      }
      // If plain text, convert newlines to <br> tags
      return details.replace(/\n/g, '<br>');
    };

    // Create HTML for each facility
    const facilitiesHTML = facilities.map((facility, index) => {
      const facilityName = facility.name || '';
      const facilityAddress = facility.address || '';
      
      // Select details based on language with fallback
      const details = language === 'ma' 
        ? (facility.detailsMr || facility.detailsEn || '')
        : (facility.detailsEn || '');
      
      const formattedDetails = formatDetails(details);
      const contact = facility.contact || '';

      return `
        <div style="margin-bottom: 32px;">
          ${facilityName ? `<h3 style="color: #2c2c2c; margin: 0 0 12px 0; font-size: 1.2rem; font-weight: 600;">${facilityName}</h3>` : ''}
          ${facilityAddress ? `<p style="color: #4a4a4a; margin: 0 0 16px 0; line-height: 1.7; font-weight: 500;"><strong>Address:</strong> ${facilityAddress}</p>` : ''}
          ${formattedDetails ? `
            <div style="margin-top: 16px; margin-bottom: 16px;">
              <div style="color: #4a4a4a; line-height: 1.7; white-space: pre-wrap;">${formattedDetails}</div>
            </div>
          ` : ''}
          ${contact ? `<p style="color: #4a4a4a; margin: 0; line-height: 1.7;"><strong>Contact:</strong> ${contact}</p>` : ''}
        </div>
      `;
    }).join('');

    return `<div style="margin: 16px 0;">${facilitiesHTML}</div>`;
  };

  // Transform FAQs to HTML format for display
  const formatFAQsAsHTML = (faqs) => {
    if (!Array.isArray(faqs) || faqs.length === 0) {
      return '<p>Content will be available soon.</p>';
    }

    const language = getLanguage();

    // Check if text contains HTML tags
    const containsHTML = (text) => {
      if (!text) return false;
      return /<[a-z][\s\S]*>/i.test(text);
    };



    // Create HTML for each FAQ in accordion style
    const faqsHTML = faqs.map((faq, index) => {
      // Select title and description based on language with fallback
      const title = language === 'ma' 
        ? (faq.titleMr || faq.title || '')
        : (faq.title || '');
      
      // Prefer HTML version if available, otherwise use plain text
      const descriptionHtml = language === 'ma'
        ? (faq.descriptionMrHtml || faq.descriptionHtml || null)
        : (faq.descriptionHtml || null);
      
      const description = language === 'ma'
        ? (faq.descriptionMr || faq.description || '')
        : (faq.description || '');

      // Handle title - check if it contains HTML formatting
      let titleContent = '';
      if (title) {
        if (containsHTML(title)) {
          // Title contains HTML, use it as-is
          titleContent = title;
        } else {
          // Plain text title, use as-is
          titleContent = title;
        }
      }
      
      // Prefer HTML version if available, otherwise check if description contains HTML
      let descriptionContent = '';
      if (descriptionHtml) {
        descriptionContent = descriptionHtml;
      } else if (description) {
        if (containsHTML(description)) {
          descriptionContent = description;
        } else {
          descriptionContent = description.replace(/\n/g, '<br>');
        }
      }

      if (!titleContent && !descriptionContent) {
        return ''; // Skip FAQs with no content
      }

      return `
        <div style="margin-bottom: 20px; border-bottom: 1px solid #e0e0e0; padding-bottom: 20px;">
          ${titleContent ? `<h3 style="color: #2c2c2c; margin: 0 0 12px 0; font-size: 1.1rem; font-weight: 600;">${titleContent}</h3>` : ''}
          ${descriptionContent ? `
            <div style="color: #4a4a4a; line-height: 1.7; margin-top: 8px;">
              ${descriptionContent}
            </div>
          ` : ''}
        </div>
      `;
    }).filter(html => html.trim() !== '').join(''); // Filter out empty FAQs

    return `<div style="margin: 16px 0;">${faqsHTML}</div>`;
  };

  // Transform schedules to HTML format for display
  const formatSchedulesAsHTML = (schedules) => {
    if (!Array.isArray(schedules) || schedules.length === 0) {
      return '<p>Content will be available soon.</p>';
    }



    // Create HTML for each schedule
    const schedulesHTML = schedules.map((schedule, index) => {
      const title = schedule.title || eventInfo?.title || '';
      const dateRange = schedule.start_date_format && schedule.end_date_format
        ? `${schedule.start_date_format} - ${schedule.end_date_format}`
        : (schedule.start_date_format || schedule.end_date_format || '');
      
      const cityName = schedule.city_name || '';
      const venueAddress = schedule.venue_address || '';
      const contactDetails = schedule.contact_details || '';
      
      // Build location string
      let locationParts = [];
      if (cityName) locationParts.push(cityName);
      if (venueAddress) locationParts.push(venueAddress);
      const location = locationParts.join(', ');
      
      // Pricing information
      const firstPrice = schedule.first_price || eventInfo?.first_price || '';
      const firstPriceFor = schedule.first_price_for || eventInfo?.first_price_for || 'Person';
      const secondPrice = schedule.second_price || eventInfo?.second_price || '';
      const secondPriceFor = schedule.second_price_for || eventInfo?.second_price_for || '';
      
      // Duration
      const duration = schedule.duration_day || schedule.total_days || eventInfo?.duration || '';
      const durationText = duration ? `${duration} Days` : '';

      return `
        <div style="margin-bottom: 32px; border-bottom: 1px solid #e0e0e0; padding-bottom: 24px;">
          ${title ? `<h3 style="color: #2c2c2c; margin: 0 0 12px 0; font-size: 1.2rem; font-weight: 600;">${title}</h3>` : ''}
          ${dateRange ? `
            <div style="margin-bottom: 12px;">
              <strong style="color: #2c2c2c;">Date:</strong> 
              <span style="color: #4a4a4a; margin-left: 8px;">${dateRange}</span>
            </div>
          ` : ''}
          ${durationText ? `
            <div style="margin-bottom: 12px;">
              <strong style="color: #2c2c2c;">Duration:</strong> 
              <span style="color: #4a4a4a; margin-left: 8px;">${durationText}</span>
            </div>
          ` : ''}
          ${location ? `
            <div style="margin-bottom: 12px;">
              <strong style="color: #2c2c2c;">Location:</strong> 
              <span style="color: #4a4a4a; margin-left: 8px;">${location}</span>
            </div>
          ` : ''}
          ${contactDetails ? `
            <div style="margin-bottom: 12px;">
              <strong style="color: #2c2c2c;">Contact:</strong> 
              <span style="color: #4a4a4a; margin-left: 8px;">${contactDetails}</span>
            </div>
          ` : ''}
          ${firstPrice ? `
            <div style="margin-bottom: 12px;">
              <strong style="color: #2c2c2c;">Price:</strong> 
              <span style="color: #4a4a4a; margin-left: 8px;">INR ${firstPrice} ${firstPriceFor ? `per ${firstPriceFor}` : ''}</span>
              ${secondPrice ? ` / INR ${secondPrice} ${secondPriceFor ? `per ${secondPriceFor}` : ''}` : ''}
            </div>
          ` : ''}
          ${schedule.is_full === 1 ? `
            <div style="margin-top: 12px;">
              <span style="color: #9d0b0e; font-weight: 600; padding: 4px 12px; background-color: #ffe0e0; border-radius: 4px; display: inline-block;">Event Full</span>
            </div>
          ` : ''}
        </div>
      `;
    }).join('');

    return `<div style="margin: 16px 0;">${schedulesHTML}</div>`;
  };

  // Transform related events to HTML format for display
  const formatRelatedEventsAsHTML = (events) => {
    if (!Array.isArray(events) || events.length === 0) {
      return '<p>Content will be available soon.</p>';
    }

    // Escape HTML in text to prevent XSS
    const escapeHtml = (text) => {
      if (!text) return '';
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    };

    // Format description text - handle Draft.js JSON and preserve line breaks
    // Also sanitize out hardcoded registration links like
    // "Link to register: https://www.manashakti.org/event_details/42"
    const formatDescription = (description) => {
      if (!description) return '';

      // First, try to parse Draft.js JSON format
      let plainText = parseDraftJsToText(description);

      // Remove explicit "Link to register:" lines and any site event_details URLs
      // e.g. "Link to register: https://www.manashakti.org/event_details/42"
      plainText = plainText.replace(/Link to register:\s*https?:\/\/[\S]+/ig, '');
      plainText = plainText.replace(/https?:\/\/((www|prepod)\.)?manashakti\.org\/event_details\/?\d*/ig, '');

      // Then convert newlines to <br> tags
      return plainText.replace(/\n/g, '<br>');
    };

    // Create HTML for each related event
    const eventsHTML = events.map((event, index) => {
      const title = event.title || '';
      const date = event.date || '';
      const location = event.location || '';
      const language = event.language || '';
      // Prepare sanitized plain text description and detect if it is long
      let rawDescPlain = parseDraftJsToText(event.description || '');
      rawDescPlain = rawDescPlain.replace(/Link to register:\s*https?:\/\/[\S]+/ig, '');
      rawDescPlain = rawDescPlain.replace(/https?:\/\/(?:www\.|prepod\.)?manashakti\.org\/event_details\/?\d*/ig, '');
      const needsTruncate = (rawDescPlain || '').length > 300;
      const description = rawDescPlain.replace(/\n/g, '<br>');
      const eventType = event.eventType || '';
      const ageGroup = event.ageGroup || '';

      // Build the view details URL to match other navigation in this page
      const viewDetailsUrl = event.eventId ? `/event_details/${event.eventId}` : '#';

      return `
        <div style="margin-bottom: 32px; border-bottom: 1px solid #e0e0e0; padding-bottom: 24px;">
          ${title ? `<h3 style="color: #2c2c2c; margin: 0 0 12px 0; font-size: 1.2rem; font-weight: 600;">${title}</h3>` : ''}
          ${eventType ? `
            <div style="margin-bottom: 8px;">
              <strong style="color: #2c2c2c;">Event Type:</strong> 
              <span style="color: #4a4a4a; margin-left: 8px;">${eventType}</span>
            </div>
          ` : ''}
          ${date ? `
            <div style="margin-bottom: 8px;">
              <strong style="color: #2c2c2c;">Date:</strong> 
              <span style="color: #4a4a4a; margin-left: 8px;">${date}</span>
            </div>
          ` : ''}
          ${location ? `
            <div style="margin-bottom: 8px;">
              <strong style="color: #2c2c2c;">Location:</strong> 
              <span style="color: #4a4a4a; margin-left: 8px;">${location}</span>
            </div>
          ` : ''}
          ${language ? `
            <div style="margin-bottom: 8px;">
              <strong style="color: #2c2c2c;">Language:</strong> 
              <span style="color: #4a4a4a; margin-left: 8px;">${language}</span>
            </div>
          ` : ''}
          ${ageGroup ? `
            <div style="margin-bottom: 8px;">
              <strong style="color: #2c2c2c;">Age Group:</strong> 
              <span style="color: #4a4a4a; margin-left: 8px;">${ageGroup}</span>
            </div>
          ` : ''}
          ${description ? `
            <div style="margin-top: 12px; position:relative;">
              <div style="color: #4a4a4a; line-height: 1.7; white-space: pre-wrap; ${needsTruncate ? 'max-height:6.6em; overflow:hidden;' : ''}">${description}</div>
              ${needsTruncate ? `<div style="position:absolute; left:0; right:0; bottom:0; height:2.2em; background:linear-gradient(rgba(255,255,255,0), rgba(255,255,255,1)); pointer-events:none;"></div>` : ''}
            </div>
          ` : ''}

          <div style="margin-top: 12px;">
            ${event.eventId ? `<a href="${viewDetailsUrl}" class="btn btn-brown btn-sm" style="text-decoration:none; padding:4px 8px; font-size:13px; line-height:1.2; display:inline-block;">View Details</a>` : ''}
          </div>

        </div>
      `;
    }).join('');

    return `<div style="margin: 16px 0;">${eventsHTML}</div>`;
  };

  // Transform topics to HTML format for display with clickable links
  const formatTopicsAsHTML = (topics, eventInfo) => {
    if (!Array.isArray(topics) || topics.length === 0) {
      return '<p>Content will be available soon.</p>';
    }

    const language = getLanguage();
    const isMarathi = language === 'ma';

    // Get event type ID from eventInfo (default to 1 for Residential Study Courses)
    const eventTypeId = eventInfo?.event_type_id || eventInfo?.eventTypeId || 1;
    
    // Get event type name for URL
    const getEventTypeName = () => {
      if (eventInfo?.event_type) {
        return eventInfo.event_type;
      }
      // Map event type ID to name
      const eventTypeMap = {
        1: 'Residential Study Courses',
        2: '1-Day Workshops',
        3: 'Machine Tests',
        4: 'Rational Poojas',
        5: 'Free Events'
      };
      return eventTypeMap[eventTypeId] || 'Residential Study Courses';
    };
    
    const eventTypeName = getEventTypeName();

    // Escape HTML in text to prevent XSS
    const escapeHtml = (text) => {
      if (!text) return '';
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    };

    // Create HTML for each topic as a clickable link
    const topicsHTML = topics.map((topic, index) => {
      // Get topic name based on language
      const topicName = isMarathi
        ? (topic.nameMr || topic.titleMr || topic.topic_title_mr || topic.name || topic.title || topic.topic_title || '')
        : (topic.nameEn || topic.titleEn || topic.topic_title_en || topic.name || topic.title || topic.topic_title || '');
      
      if (!topicName) return '';

      // Build navigation URL
      const categoryName = encodeURIComponent(eventTypeName);
      const navigateUrl = `/booknow/${categoryName}/${eventTypeId}`;
      
      // Create clickable link that will navigate with topic in location state
      // We'll use a data attribute to store the topic name for JavaScript navigation
      return `
        <a 
          href="${navigateUrl}" 
          class="topic-link" 
          data-topic-name="${topicName}"
          data-topic-id="${topic.id || topic.topic_id || ''}"
        >
          ${topicName}
        </a>
      `;
    }).filter(html => html.trim() !== '').join('');

    return `<div style="margin: 16px 0;">${topicsHTML}</div>`;
  };

  // Transform instructions to HTML format for display
  const formatInstructionsAsHTML = (instructions) => {
    if (!Array.isArray(instructions) || instructions.length === 0) {
      return '<p>Content will be available soon.</p>';
    }

    const language = getLanguage();
    const isMarathi = language === 'ma';
    
    // Filter instructions by language if needed
    // languageId: 1 = English, 2 = Marathi, 3 = Other, null = not specified
    // Each instruction record contains both instructionText (English) and instructionTextMr (Marathi).
    // All records are shown regardless of languageId — the correct text field is chosen per language below.
    const instructionsToDisplay = instructions;

    // Check if text contains HTML tags
    const containsHTML = (text) => {
      if (!text) return false;
      // Simple check for HTML tags
      return /<[a-z][\s\S]*>/i.test(text);
    };

    // Format instruction text - prefer HTML field if present, else handle both HTML and plain text
    const formatInstructionText = (instruction) => {
      if (!instruction) return '';
      // Prefer HTML field if present
      if (isMarathi && instruction.instructionTextMrHtml) {
        return instruction.instructionTextMrHtml;
      }
      if (!isMarathi && instruction.instructionTextHtml) {
        return instruction.instructionTextHtml;
      }
      // Fallback to text fields
      const text = isMarathi
        ? (instruction.instructionTextMr || instruction.instructionText || '')
        : (instruction.instructionText || instruction.instructionTextMr || '');
      if (containsHTML(text)) {
        return text;
      }
      return text.replace(/\n/g, '<br>');
    };

    // If any instruction has HTML, join and return only the HTML fields (no extra wrapping)
    const htmlList = instructionsToDisplay
      .map(inst => isMarathi ? inst.instructionTextMrHtml : inst.instructionTextHtml)
      .filter(Boolean);
    if (htmlList.length > 0) {
      return htmlList.join('<br/>');
    }
    // Otherwise, format each instruction as before
    const instructionsHTML = instructionsToDisplay.map((instruction, index) => {
      const instructionText = formatInstructionText(instruction);
      if (!instructionText) return '';
      return `
        <div style="margin-bottom: 24px; padding-bottom: 16px; border-bottom: ${index < instructionsToDisplay.length - 1 ? '1px solid #e0e0e0' : 'none'};">
          <div style="color: #4a4a4a; line-height: 1.7; font-size: 0.875rem;">
            ${instructionText}
          </div>
        </div>
      `;
    }).filter(html => html.trim() !== '').join('');
    return `<div style="margin: 16px 0;">${instructionsHTML}</div>`;
  };

  // Format event topics from API for display (handles topicTitle and topicTitleMr fields)
  const formatEventTopicsAsHTML = (topics) => {
    if (!Array.isArray(topics) || topics.length === 0) {
      return '<p>Content will be available soon.</p>';
    }

    const language = getLanguage();
    const isMarathi = language === 'ma';

    // Check if text contains HTML tags
    const containsHTML = (text) => {
      if (!text) return false;
      return /<[a-z][\s\S]*>/i.test(text);
    };

    // Format topic text - handle both HTML and plain text
    const formatTopicText = (text) => {
      if (!text) return '';
      // If text already contains HTML, use it as-is
      if (containsHTML(text)) {
        return text;
      }
      // If plain text, convert newlines to <br> tags
      return text.replace(/\n/g, '<br>');
    };

    // Create HTML for each topic
    const topicsHTML = topics.map((topic, index) => {
      const rawText = isMarathi
        ? (topic.topicTitleMr || topic.topicTitle || '')
        : (topic.topicTitle || topic.topicTitleMr || '');
      const topicText = formatTopicText(rawText);
      
      if (!topicText) return '';

      return `
        <div style="margin-bottom: 24px; padding-bottom: 16px; border-bottom: ${index < topics.length - 1 ? '1px solid #e0e0e0' : 'none'};">
          <div style="color: #4a4a4a; line-height: 1.7; font-size: 0.875rem;">
            ${topicText}
          </div>
        </div>
      `;
    }).filter(html => html.trim() !== '').join('');

    return `<div style="margin: 16px 0;">${topicsHTML}</div>`;
  };

  // Format schedule descriptions from API for display (handles description and descriptionMr fields)
  const formatScheduleDetailsAsHTML = (schedules) => {
    if (!Array.isArray(schedules) || schedules.length === 0) {
      return '<p>Content will be available soon.</p>';
    }

    const language = getLanguage();
    const isMarathi = language === 'ma';

    // Check if text contains HTML tags
    const containsHTML = (text) => {
      if (!text) return false;
      return /<[a-z][\s\S]*>/i.test(text);
    };

    // Format schedule text - handle both HTML and plain text
    const formatScheduleText = (text) => {
      if (!text) return '';
      
      // If text already contains HTML, use it as-is
      if (containsHTML(text)) {
        return text;
      }
      
      // If plain text, convert newlines to <br> tags
      const escapeHtml = (txt) => {
        if (!txt) return '';
        return txt
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;');
      };
      
      const escaped = escapeHtml(text);
      return escaped.replace(/\n/g, '<br>');
    };

    // Create HTML for each schedule description
    const schedulesHTML = schedules
      .filter(schedule => {
        // Filter to only show active schedules with status = true
        return schedule.status !== false && !schedule.isDeleted;
      })
      .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
      .map((schedule, index, filteredSchedules) => {
        const rawText = isMarathi
          ? (schedule.descriptionMr || schedule.description || '')
          : (schedule.description || schedule.descriptionMr || '');
        const scheduleText = formatScheduleText(rawText);

        const imageUrl = isMarathi
          ? (schedule.imageLinkMr || schedule.image_link_mr || schedule.imageLink || schedule.image_link || '')
          : (schedule.imageLink || schedule.image_link || schedule.imageLinkMr || schedule.image_link_mr || '');

        if (!scheduleText && !imageUrl) return '';

        const imageHTML = imageUrl
          ? `<div style="margin-bottom: 12px;">
               <img src="${imageUrl}" alt="" style="max-width: 100%; border-radius: 8px; display: block;" onerror="this.style.display='none'" />
             </div>`
          : '';

        return `
          <div style="margin-bottom: 24px; padding-bottom: 16px; border-bottom: ${index < filteredSchedules.length - 1 ? '1px solid #e0e0e0' : 'none'};">
            ${imageHTML}
            ${scheduleText ? `<div style="color: #4a4a4a; line-height: 1.7; font-size: 0.875rem;">${scheduleText}</div>` : ''}
          </div>
        `;
      })
      .filter(html => html.trim() !== '')
      .join('');

    return schedulesHTML.length > 0 
      ? `<div style="margin: 16px 0;">${schedulesHTML}</div>`
      : '<p>Content will be available soon.</p>';
  };

  // Transform videos to ReactImageVideoLightbox format
  // The new API (/event/media/videoPlaylist/videos) returns objects with thumbnail, title, embedUrl, videoUrl directly
  const transformVideosForLightbox = (videoList) => {
    return videoList.map(video => {
      // Prefer embedUrl from API, fallback to videoUrl
      const url = video.embedUrl || video.videoUrl || video.url || '';
      const parsedTitle = parseDraftJsToText(video.title) || 'Event Video';
      return {
        url: url,
        type: 'video',
        altTag: parsedTitle
      };
    });
  };

  // Transform testimonials to ReactImageVideoLightbox format
  const transformTestimonialsForLightbox = (testimonialList) => {
    return testimonialList.map(testimonial => {
      // testimonial may already be transformed by transformTestimonialData
      // prefer existing play_url or video_url fields if present
      const hasPlayUrl = testimonial && (testimonial.play_url || testimonial.video_url || testimonial.videoUrl);
      let url = '';

      if (hasPlayUrl) {
        url = testimonial.play_url || testimonial.video_url || testimonial.videoUrl || '';
      } else {
        // fallback: transform raw testimonial object
        const transformed = transformTestimonialData(testimonial);
        url = transformed.play_url || transformed.video_url || '';
      }

      // Normalize to YouTube embed when possible
      const embedUrl = getYouTubeEmbedUrl(url);

      return {
        url: embedUrl || url,
        type: 'video',
        altTag: (testimonial && (testimonial.title || testimonial.participantName)) || 'Testimonial'
      };
    });
  };

  // Open video lightbox
  const openVideoLightbox = (videoIndex, videoList, isTestimonial = false) => {
    let videoData;
    if (isTestimonial) {
      videoData = transformTestimonialsForLightbox(videoList);
    } else {
      videoData = transformVideosForLightbox(videoList);
    }
    
    setLightboxVideoData(videoData);
    setLightboxStartIndex(videoIndex);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  // Close video lightbox
  const closeVideoLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
  };

  // Check if machine test is in cart (for both logged-in and guest users)
  const checkMachineTestInCart = React.useCallback((testEventId) => {
    const userId = localStorage.getItem('user_id');
    
    if (!userId || userId === '0') {
      // Guest user - check localStorage
      try {
        const guestMtCart = JSON.parse(localStorage.getItem('guest_mt_cart') || '[]');
        return guestMtCart.some(item => item.event_id == testEventId);
      } catch (error) {
        return false;
      }
    } else {
      // For logged-in users, we'll need to rely on the schedules data from API
      // which should have added_in_cart info
      return false; // Will be updated from schedules data
    }
  }, []);
  
  // Update machine tests cart status
  const updateMachineTestsCartStatus = React.useCallback(() => {
    if (schedules && schedules.length > 0) {
      const cartStatus = {};
      schedules.forEach(schedule => {
        const scheduleEventId = schedule.event_id || schedule.schedule_id || schedule.scheduleId;
        if (scheduleEventId) {
          // Check if it's in cart (for guests via localStorage, for logged-in via API data)
          const isInCart = checkMachineTestInCart(scheduleEventId) || 
            (schedule.added_in_cart && schedule.added_in_cart.length > 0);
          cartStatus[scheduleEventId] = isInCart;
        }
      });
      setMachineTestsInCart(cartStatus);
    }
  }, [schedules, checkMachineTestInCart]);
  
  // Update cart status when schedules change
  React.useEffect(() => {
    updateMachineTestsCartStatus();
  }, [updateMachineTestsCartStatus]);

  // Machine test add to cart handler - shows modal first
  const handleMachineTestAddToCart = (eventId, parent, child, schedule) => {
    if (parent <= 0) {
      const message = getLanguage() === 'ma' ? 'कृपया सहभागींची संख्या निवडा.' : 'Please select the number of participants.';
      toast.error(message);
      return;
    }

    // Store data temporarily and show modal
    setTempCartData({
      eventId,
      parent,
      child,
      schedule
    });

    // Show the appointment alert modal
    $('#MachineTestModal').modal('show');
  };

  // Continue adding to cart after modal confirmation
  const addToCartContinue = async () => {
    // Hide modal
    $('#MachineTestModal').modal('hide');

    if (!tempCartData) return;

    const { eventId, parent, child, schedule } = tempCartData;
    const userId = localStorage.getItem('user_id');

    if (!userId || userId === '0') {
      // Guest user - store in guest_mt_cart
      try {
        const guestMtCart = JSON.parse(localStorage.getItem('guest_mt_cart') || '[]');
        const existingIndex = guestMtCart.findIndex(item => item.event_id == eventId);
        
        const item = {
          event_id: eventId,
          parent: parent,
          child: child,
          title: eventInfo?.title || schedule.title || 'Machine Test',
          price: eventInfo?.first_price || schedule.first_price || '0',
          first_price: eventInfo?.first_price || schedule.first_price || '0',
          second_price: schedule.second_price || '0',
          child_price: schedule.child_price || '0',
          first_price_for: eventInfo?.first_price_for || schedule.first_price_for || 'Person',
          event_type: 'Machine Test',
          event_type_id: 3,
          added_at: new Date().toISOString()
        };

        if (existingIndex !== -1) {
          guestMtCart[existingIndex] = item;
        } else {
          guestMtCart.push(item);
        }

        localStorage.setItem('guest_mt_cart', JSON.stringify(guestMtCart));
        updateGuestCartDisplay();
        
        // Update cart status to show ADD MORE
        setMachineTestsInCart(prev => ({ ...prev, [eventId]: true }));
        
        toast.success('Machine test added in cart');
      } catch (error) {
        toast.error('Unable to add machine test to cart');
      }
    } else {
      // Logged-in user - use API
      try {
        const url = myConstClass.BASE_URL + `cart/addMachineTestEventToCart/${eventId}/${userId}/${parent}/${child}`;
        const response = await fetch(url, { method: 'GET' });
        const data = await response.json();
        
        if (data.status === 'true') {
          toast.success('Machine test added in cart');
          
          // Update cart status to show ADD MORE
          setMachineTestsInCart(prev => ({ ...prev, [eventId]: true }));
          
          // Refresh header cart count using the fixed getCartDisplayCount
          getCartDisplayCount(userId).then(count => {
            document.querySelectorAll('.lblCartCount').forEach(el => {
              el.textContent = count;
            });
            // Cache the count for immediate retrieval
            localStorage.setItem('cached_cart_count', count.toString());
          }).catch(() => {});
        } else {
          toast.error(data.message || 'Failed to add machine test to cart');
        }
      } catch (error) {
        toast.error('Unable to add machine test to cart');
      }
    }

    // Clear temp data
    setTempCartData(null);
  };

  // =======================
// LIKE / DISLIKE HANDLERS
// =======================

const handleLike = () => {
  const userId = localStorage.getItem('user_id');

  // 🔐 Guest → login
  if (!userId || userId === '0') {
    localStorage.setItem('redirect_after_login', window.location.pathname);
    history.push('/signin');
    return;
  }

  // ❌ SAME ACTION GUARD
  if (userReaction === 'like') {
    toast.info('You have already liked this event');
    return;
  }

  if (likeLoading) return;
  setLikeLoading(true);

  // 🔥 OPTIMISTIC UI UPDATE
  setLikes(prev => prev + 1);
  if (userReaction === 'dislike') {
    setDislikes(prev => Math.max(prev - 1, 0));
  }
  setUserReaction('like');

  fetch(`${myConstClass.NODE_API_BASE}/event-like-dislike`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      product_event_id_fk: eventId,
      user_id: userId,
      action: 'like'
    })
  })
    .then(res => res.json())
    .then(res => {
      if (res.status !== 'true') {
        // ⛔ rollback
        setLikes(prev => Math.max(prev - 1, 0));
        if (userReaction === 'dislike') setDislikes(prev => prev + 1);
        setUserReaction(null);
        toast.error('Unable to like event');
      }
    })
    .catch(() => {
      setLikes(prev => Math.max(prev - 1, 0));
      if (userReaction === 'dislike') setDislikes(prev => prev + 1);
      setUserReaction(null);
      toast.error('Network error');
    })
    .finally(() => setLikeLoading(false));
};


const handleDislike = () => {
  const userId = localStorage.getItem('user_id');

  // 🔐 Guest → login
  if (!userId || userId === '0') {
    localStorage.setItem('redirect_after_login', window.location.pathname);
    history.push('/signin');
    return;
  }

  // ❌ SAME ACTION GUARD
  if (userReaction === 'dislike') {
    toast.info('You have already disliked this event');
    return;
  }

  if (likeLoading) return;
  setLikeLoading(true);

  // 🔥 OPTIMISTIC UI UPDATE
  setDislikes(prev => prev + 1);
  if (userReaction === 'like') {
    setLikes(prev => Math.max(prev - 1, 0));
  }
  setUserReaction('dislike');

  fetch(`${myConstClass.NODE_API_BASE}/event-like-dislike`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      product_event_id_fk: eventId,
      user_id: userId,
      action: 'dislike'
    })
  })
    .then(res => res.json())
    .then(res => {
      if (res.status !== 'true') {
        // ⛔ rollback
        setDislikes(prev => Math.max(prev - 1, 0));
        if (userReaction === 'like') setLikes(prev => prev + 1);
        setUserReaction(null);
        toast.error('Unable to dislike event');
      }
    })
    .catch(() => {
      setDislikes(prev => Math.max(prev - 1, 0));
      if (userReaction === 'like') setLikes(prev => prev + 1);
      setUserReaction(null);
      toast.error('Network error');
    })
    .finally(() => setLikeLoading(false));
};



  const handleShare = (schedule) => {
    const shareData = {
      title: schedule?.title || eventInfo?.title,
      text: schedule?.city_name,
      url: window.location.href,
    };

    if (navigator.share) {
      navigator.share(shareData);
      return;
    }

    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(shareData.url)
        .then(() => toast.success('Link copied to clipboard'))
        .catch(() => toast.info('Copy this link: ' + shareData.url));
      return;
    }

    toast.info('Copy this link: ' + shareData.url);
  };

  const handleBookNow = (schedule) => {
    if (!eventId) return;
    if (!schedule || !schedule.schedule_id) {
      toast.error('Schedule information is missing');
      return;
    }

    const user_id = localStorage.getItem('user_id');
    const language = getLanguage();

    // Create event booking data object with pricing info (participants will be added later)
    // Also store topic_id if available
    let topic_id = null;
    if (eventInfo?.topicId) {
      topic_id = eventInfo.topicId;
    } else if (Array.isArray(eventInfo?.topics) && eventInfo.topics.length > 0) {
      topic_id = eventInfo.topics[0].id || eventInfo.topics[0].topic_id || null;
    }

    const eventBookingData = {
      event_id: eventId,
      schedule_id: schedule.schedule_id,
      parent: 0,  // Will be calculated from participants on eventDetails page
      child: 0,   // Will be calculated from participants on eventDetails page
      title: schedule.title || eventInfo?.title || 'Event Booking',
      title_mr: eventInfo?.title_mr || schedule.title || eventInfo?.title || 'Event Booking',
      price: schedule.first_price || eventInfo?.first_price || '0',
      child_price: schedule.second_price || eventInfo?.second_price || eventInfo?.first_price || '0',
      first_price_for: schedule.first_price_for || eventInfo?.first_price_for || 'Person',
      second_price_for: schedule.second_price_for || eventInfo?.second_price_for || '',
      event_type: eventInfo?.event_type || 'Event',
      event_type_id: eventInfo?.event_type_id || eventInfo?.eventTypeId || 1,
      image: eventInfo?.image_name || eventInfo?.image || '',
      start_date: schedule.start_date || schedule.startDateOriginal || '',
      end_date: schedule.end_date || schedule.endDateOriginal || '',
      venue: schedule.city_name || '',
      venue_address: schedule.venue_address || '',
      selected_at: new Date().toISOString(),
      topic_id: topic_id
    };

    // For machine test events, add to guest_cart_products (product cart) for both guests and logged-in users
    const isMachineTest = eventInfo?.event_type_id == 3 || (eventInfo?.event_type || '').toLowerCase().includes('machine');
    if (isMachineTest) {
      try {
        const guestMtCart = JSON.parse(localStorage.getItem('guest_mt_cart') || '[]');
        const eventId = eventBookingData.event_id;
        const exists = guestMtCart.find(g => g.event_id == eventId);
        if (!exists) {
          guestMtCart.push({ ...eventBookingData, added_at: new Date().toISOString() });
        } else {
          // Update parent/child counts if needed
          exists.parent = eventBookingData.parent;
          exists.child = eventBookingData.child;
        }
        localStorage.setItem('guest_mt_cart', JSON.stringify(guestMtCart));
        toast.success('Added to cart. Login to persist.', { containerId: 'B' });
      } catch (err) {
        toast.error('Error saving to cart', { containerId: 'B' });
      }
      return;
    } else {
      // This REPLACES any previous event (only one event booking at a time)
      if (user_id && user_id > 0) {
        // Logged-in user
        localStorage.setItem('booking_event', JSON.stringify(eventBookingData));
      } else {
        // Guest user
        localStorage.setItem('booking_event_guest', JSON.stringify(eventBookingData));
      }
    }
    
    // Redirect to event details page for participant selection
    setTimeout(() => {
      history.push('/eventDetails');
    }, 100);
  };

  const scrollSchedulesLeft = () => {
    if (scheduleGridRef.current) {
      scheduleGridRef.current.scrollBy({ left: -280, behavior: 'smooth' });
    }
  };

  const scrollSchedulesRight = () => {
    if (scheduleGridRef.current) {
      scheduleGridRef.current.scrollBy({ left: 280, behavior: 'smooth' });
    }
  };

  const handleAddProductToCart = async (productId, quantity) => {
    const userId = localStorage.getItem('user_id');
    
    if (quantity <= 0 || !productId) {
      toast.error('Invalid product quantity');
      return;
    }
    
    if (!userId || userId === '0') {
      // Guest user - store in localStorage
      try {
        // Check for cart conflicts - products can't mix with magazines/events
        const conflictMessage = getGuestCartConflictMessage('product', getLanguage);
        if (conflictMessage) {
          toast.error(conflictMessage);
          return;
        }
        
        const guestCart = JSON.parse(localStorage.getItem('guest_cart_products') || '[]');
        const product = products.find(p => p.product_id == productId);
        
        if (!product) {
          toast.error('Product not found');
          return;
        }
        
        const existingIndex = guestCart.findIndex(item => item.product_id == productId);
        
        if (existingIndex !== -1) {
          guestCart[existingIndex].quantity = parseInt(guestCart[existingIndex].quantity) + parseInt(quantity);
        } else {
          guestCart.push({
            product_id: productId,
            quantity: parseInt(quantity),
            title: product.name,
            price: product.price,
            image_name: product.image,
            added_at: new Date().toISOString(),
          });
        }
        
        localStorage.setItem('guest_cart_products', JSON.stringify(guestCart));
        
        // Update cart display
        updateGuestCartDisplay();
        
        // Update product state to show "Add More" button
        setProducts(prevProducts => 
          prevProducts.map(p => 
            p.product_id == productId ? { ...p, isInCart: true } : p
          )
        );
        
        toast.success('Product added to cart successfully');
      } catch (error) {
        toast.error('Unable to add product to cart');
      }
    } else {
      // Logged-in user - make API call
      try {
        // Check if cart has magazines or events - if so, show conflict message
        const [hasMagazines, hasEvents] = await Promise.all([
          cartHasMagazines(userId),
          cartHasEvents(userId)
        ]);
        
        if (hasMagazines || hasEvents) {
          const conflictMessage = getCartConflictMessage(false, hasMagazines, hasEvents, getLanguage);
          toast.error(conflictMessage);
          return;
        }
        
        // IMPORTANT: Parameter order is product_id / quantity / user_id
        fetch(`${myConstClass.BASE_URL}cart/addProductToCart/${productId}/${quantity}/${userId}`)
          .then(res => res.json())
          .then(response => {
            const message = response.message;
            const status = response.status;
            
            if (status === 'true') {
              toast.success(message || 'Product added to cart successfully');
              
              // Update product state to show "Add More" button
              setProducts(prevProducts => 
                prevProducts.map(p => 
                  p.product_id == productId ? { ...p, isInCart: true } : p
                )
              );
              
              // Update cart count in header using the fixed getCartDisplayCount
              getCartDisplayCount(userId).then(count => {
                document.querySelectorAll('.lblCartCount').forEach(el => {
                  el.textContent = count;
                });
              }).catch(() => {});
            } else {
              toast.error(message || 'Failed to add product to cart');
            }
          })
          .catch(error => {
            toast.error('Unable to add product to cart');
          });
      } catch (error) {
        toast.error('Unable to add product to cart');
      }
    }
  };

  // hero snippet: use original event description (short) and show a small line/snippet
  const heroSnippet = useMemo(() => {
    const source = eventInfo?.short_description || eventInfo?.about_course || eventInfo?.description || '';
    const snippet = getSnippetFromContent(source, 120) || 
    "For couples planning or expecting - Empower your baby's development through prenatal education";

    // Replace &nbsp; with normal spaces
    return snippet.replace(/&nbsp;/gi, ' ');
  }, [eventInfo]);

  // Determine hero title/subtitle so header background always renders while loading
  const heroTitle = loading && !eventInfo
    ? (<span style={{ fontSize: '20px', fontWeight: 500 }}>Loading{loadingDots}</span>)
    : (eventInfo?.title || 'Event Details');
  const heroSubtitle = loading && !eventInfo ? '' : heroSnippet;

  // When a specific topic was explicitly chosen (via URL param or navigation state), find it
  // inside event.topics[] so we use the correct name/id — otherwise fall back to topics[0].
  const topicInfoFromEvent = useMemo(() => {
    if (!eventInfo) return null;
    // If caller passed a topicId, find it in the topics array to use correct name/id
    if (passedTopicId && Array.isArray(eventInfo.topics) && eventInfo.topics.length > 0) {
      const matched = eventInfo.topics.find(t => {
        const id = t.topicId || t.id || t.topic_id;
        return id && String(id) === String(passedTopicId);
      });
      if (matched) {
        const currentLang = getLanguage();
        const isMarathi = currentLang === 'ma';
        const id = matched.topicId || matched.id || matched.topic_id;
        const name = isMarathi
          ? (matched.titleMr || matched.nameMr || matched.topic_title_mr || matched.titleEn || matched.nameEn || matched.name || matched.title || '')
          : (matched.titleEn || matched.nameEn || matched.topic_title_en || matched.titleMr || matched.nameMr || matched.name || matched.title || '');
        return { displayName: formatTopicNameForDisplay(name), originalName: name, topicId: id };
      }
    }
    // Default: derive from event object (returns topics[0])
    return getTopicInfoForEvent(eventInfo, filterOptions);
  }, [eventInfo, filterOptions, passedTopicId]);

  const topicInfo = topicInfoFromEvent || (passedTopicTitle && passedTopicId ? {
    displayName: formatTopicNameForDisplay(passedTopicTitle),
    originalName: passedTopicTitle,
    topicId: passedTopicId
  } : null);
  const topicLabelForHero = topicInfo ? topicInfo.displayName : '';
  const topicIdForHero = topicInfo ? topicInfo.topicId : null;
  const topicColorForHero = getEventCategoryColor(topicInfo ? (topicInfo.originalName || topicInfo.displayName) : '');

  // Debug: Log what is being passed to EventHeroBanner

  //Defining section title according to type event/test
  const isMachineTest =
  eventInfo?.event_type_id == 3 ||
  (eventInfo?.event_type || '').toLowerCase().includes('machine');

  const aboutSectionTitle = isMachineTest
    ? 'About the Test'
    : 'About the Event';

  // aboutText should use about_course (which contains descriptionHtml from API)
  // introText should use description (which contains introductionHtml from API)
  const aboutText = eventInfo?.about_course || eventInfo?.short_description;
  const introText = eventInfo?.description;

  // -------- Tabs configuration --------
  const { t } = props;
  const allTabs = [
    {
      id: 'why-attend',
      label: t('eventTabs.why_attend'),
      content: loadingWhyAttend 
        ? '<p>Loading...</p>' 
        : whyAttendItems.length > 0 
          ? formatWhyAttendAsHTML(whyAttendItems)
          : (eventInfo?.why_attend || eventInfo?.benefits || '<p>Content will be available soon.</p>'),
    },
    {
      id: 'instructions',
      label: t('eventTabs.instructions'),
      content: loadingInstructions 
        ? '<p>Loading...</p>' 
        : instructionItems.length > 0 
          ? formatInstructionsAsHTML(instructionItems)
          : (eventInfo?.instructions || eventInfo?.guidelines || '<p>Content will be available soon.</p>'),
    },
    {
      id: 'venue',
      label: t('eventTabs.how_to_reach'),
      content: loadingVenues 
        ? '<p>Loading...</p>' 
        : venueItems.length > 0 
          ? formatVenuesAsHTML(venueItems)
          : (eventInfo?.venue_directions || eventInfo?.how_to_reach || '<p>Content will be available soon.</p>'),
    },
    {
      id: 'stay',
      label: t('eventTabs.stay_facilities'),
      content: loadingStayFacilities 
        ? '<p>Loading...</p>' 
        : stayFacilities.length > 0 
          ? formatStayFacilitiesAsHTML(stayFacilities)
          : (eventInfo?.stay_facilities || eventInfo?.accommodation || '<p>Content will be available soon.</p>'),
    },
    {
      id: 'topics',
      label: t('eventTabs.topics'),
      content: loadingTopics 
        ? '<p>Loading...</p>' 
        : topicItems.length > 0 
          ? formatEventTopicsAsHTML(topicItems)
          : '<p>Content will be available soon.</p>',
    },
    {
      id: 'schedule',
      label: t('eventTabs.schedule'),
      content: loadingScheduleDesc 
        ? '<p>Loading...</p>' 
        : scheduleDescItems.length > 0 
          ? formatScheduleDetailsAsHTML(scheduleDescItems)
          : '<p>Content will be available soon.</p>',
    },
    {
      id: 'faq',
      label: t('eventTabs.faq'),
      content: loadingFaqs 
        ? '<p>Loading...</p>' 
        : faqItems.length > 0 
          ? formatFAQsAsHTML(faqItems)
          : '<p>Content will be available soon.</p>',
    },
    {
      id: 'related-events',
      label: t('eventTabs.related_events'),
      content: loadingRelatedEvents 
        ? '<p>Loading...</p>' 
        : relatedEvents.length > 0 
          ? formatRelatedEventsAsHTML(relatedEvents)
          : '<p>Content will be available soon.</p>',
    },
  ];

  // Hide some tabs for machine tests
  const tabsToShow = isMachineTest
    ? allTabs.filter(tab =>
        !['stay', 'topics', 'schedule'].includes(tab.id)
      )
    : allTabs;


  const languageButtons = useMemo(() => {
    if (!eventInfo) {
      return [];
    }
    const preferredLanguages = ['Marathi', 'Hindi'];
    const availableLanguages = eventInfo.arr_event_languages || [];

    return preferredLanguages.map((langLabel) => {
      const match = availableLanguages.find(
        (lang) => lang.label?.toLowerCase() === langLabel.toLowerCase()
      );

      const buttonLabel =
        match?.event_title || match?.title || `${eventInfo.title || ''} (${langLabel})`;

      return {
        id: match?.value || langLabel.toLowerCase(),
        label: buttonLabel,
        onSelect: match
          ? () => history.push(eventInfo.event_type_id == 3 ? `/booknow/${match.value}/${eventInfo.event_type_id}` : `/events/${match.value}/${eventInfo.event_type_id}`)
          : undefined,
      };
    });
  }, [eventInfo, history]);

  // Memoized community WhatsApp link (fetched earlier in file)
  const whatsappCommunityLink = useMemo(() => eventWhatsappLink || null, [eventWhatsappLink]);

  // Helper to check if WhatsApp link is valid (same as in CommunityCallToAction)
  const isValidWhatsAppLink = (link) => {
    if (!link) return false;
    if (typeof link !== 'string') return false;
    const trimmed = link.trim();
    if (!trimmed) return false;
    const lower = trimmed.toLowerCase();
    if (trimmed === '#' || lower.includes('your-community-link') || lower === 'null') return false;
    return true;
  };

  return (
    <div className="event-details-page">
      <ToastContainer />
      <NewHeader />

      <EventHeroBanner
        title={heroTitle}
        subtitle={heroSubtitle}
        reviewCount={
          // Prioritize actual fetched reviews count from API pagination
          // This ensures we only show count of approved/published reviews
          reviewsPagination?.totalCount !== undefined ? reviewsPagination.totalCount :
          reviewsPagination?.total !== undefined ? reviewsPagination.total :
          reviews.length > 0 ? reviews.length :
          // Only use eventInfo.total_review as fallback if reviews haven't been fetched yet
          (reviewsPagination === null && eventInfo?.total_review) ? eventInfo.total_review :
          0
        }
        
        likes={likes}
        dislikes={null}
        userReaction={userReaction}
        onLike={handleLike}
        onDislike={handleDislike}
        topicLabel={topicLabelForHero}
        topicId={topicIdForHero}
        topicColor={topicColorForHero}
      />

      <main className="event-details-page__main container-xl">

        {/* If this is a machine test event, show the appointment instruction ABOVE the Upcoming Events heading */}
              {eventInfo && (eventInfo.event_type_id == 3 || (eventInfo.event_type || '').toLowerCase().includes('machine')) && (
                  <div className="machine-test-alert">
                    <div className="mt-title">Please call and take an appointment before booking the Test.</div>
                  <div className="mt-line"><strong>Contact on:</strong> Monday to Friday, 9:00 am to 12:00 noon</div>
                  <div className="mt-line"><strong>Phone:</strong> (02114) 234320, 234321, 234322; Mb.- 89751 57035</div>
                </div>
              )}
        <section className="event-details-page__section">
          <div className="event-details-page__content-grid">
            <div className="event-details-page__schedule-list">
              {(() => {
                const isMachineTest = eventInfo && (eventInfo.event_type_id == 3 || (eventInfo.event_type || '').toLowerCase().includes('machine'));
                if (isMachineTest) {
                  const hasSchedules = Array.isArray(schedules) && schedules.length > 0;
                  const fallbackSchedule = {
                    schedule_id: eventInfo?.event_id || eventInfo?.eventId || '0',
                    event_id: eventInfo?.event_id || eventInfo?.eventId || '0',
                    title: eventInfo?.title || 'Machine Test',
                    start_date_format: eventInfo?.start_date || eventInfo?.startDate || '',
                    city_name: eventInfo?.city_name || eventInfo?.trust_name || '',
                    first_price: eventInfo?.first_price || eventInfo?.firstPrice || '0',
                    first_price_for: eventInfo?.first_price_for || eventInfo?.firstPriceFor || 'Person'
                  };
                  const machineSchedules = hasSchedules ? schedules : [fallbackSchedule];
                  return (
                    <div className="event-details-page__schedule-grid" ref={scheduleGridRef}>
                      <MachineTestWidget 
                        schedules={machineSchedules} 
                        eventInfo={eventInfo} 
                        onAddToCart={handleMachineTestAddToCart}
                        machineTestsInCart={machineTestsInCart}
                      />
                    </div>
                  );
                }

                // Non-machine-test: render the original Upcoming Events section
                return (
                  <>
                    <div className="event-details-page__section-heading">
                      <img 
                        src={calendarIcon}
                        alt=""
                        className="event-details-page__calendar-icon"
                        aria-hidden="true"
                      />
                      <h2>Upcoming Events</h2>
                    </div>
                    {loading && <p className="event-details-page__loading">Loading schedule…</p>}
                    {!loading && schedules.length === 0 && (
                      <>
                        <p className="event-details-page__empty">No upcoming events available.</p>
                        <p className="event-details-page__empty" style={{ fontWeight: 600 }}>Scroll down to view related content.</p>
                      </>
                    )}
                    <div className="event-details-page__schedule-grid" ref={scheduleGridRef}>
                      {schedules
                        .slice() // Create a copy to avoid mutating original array
                        .sort((a, b) => {
                          // Sort so selected/shared schedule appears first
                          const aIsSelected = selectedScheduleId && a.schedule_id == selectedScheduleId;
                          const bIsSelected = selectedScheduleId && b.schedule_id == selectedScheduleId;
                          if (aIsSelected && !bIsSelected) return -1;
                          if (!aIsSelected && bIsSelected) return 1;
                          return 0; // Keep original order for others
                        })
                        .map((schedule) => {
                        const durationValue = schedule.duration_day || schedule.total_days || eventInfo?.duration || '3';
                        // Use the resolved topicInfo (which correctly respects passedTopicId from navigation)
                        // to avoid showing the wrong topic icon/color when an event belongs to multiple topics.
                        const topicIdValue = topicIdForHero;
                        const topicLabel = topicInfo?.displayName || topicInfo?.originalName || '';
                        const topicNameValue = topicInfo?.originalName || topicInfo?.displayName || '';
                        return (
                          <EventScheduleCard
                            key={schedule.schedule_id}
                            schedule_id={schedule.schedule_id}
                            event_id={schedule.event_id || eventInfo?.event_id || eventInfo?.eventId}
                            title={schedule.title || eventInfo?.title}
                            dateRange={`${schedule.start_date_format} - ${schedule.end_date_format}`}
                            start_date={schedule.start_date || schedule.startDateOriginal}
                            location={`${schedule.city_name || ''}${schedule.venue_address ? `, ${schedule.venue_address}` : ''}`}
                            duration={`${durationValue} Days`}
                            price={eventInfo?.first_price || schedule.first_price || '0'}
                            priceLabel={eventInfo?.first_price_for || 'per Person'}
                            secondPrice={schedule.second_price || eventInfo?.second_price || ''}
                            secondPriceFor={schedule.second_price_for || eventInfo?.second_price_for || ''}
                            subtopics={eventSubtopics}
                            categoryLabel={topicLabel}
                            topicName={topicNameValue}
                            topicId={topicIdValue}
                            language={getLanguageLabel(eventInfo?.languageId)}
                            currencyLabel="INR"
                            isFeatured={schedule.is_featured === 1}
                            isSelected={selectedScheduleId && schedule.schedule_id == selectedScheduleId}
                            ctaLabel="Book Now"
                            onBookNow={() => handleBookNow(schedule)}
                            onShare={() => handleShare(schedule)}
                            eventDescription={aboutText || introText || eventInfo?.about_course || eventInfo?.description || ''}
                            eventTypeId={eventInfo?.eventTypeIdFk || eventInfo?.event_type_id || eventInfo?.eventTypeId || null}
                            fullAddress={schedule.venue_address || ''}
                            googleMapsUrl={schedule.url || ''}
                          />
                        );
                      })}
                    </div>
                    {schedules.length > 0 && (
                      <div className="event-details-page__schedule-nav">
                        <button
                          className="event-details-page__nav-arrow event-details-page__nav-arrow--left"
                          onClick={scrollSchedulesLeft}
                          aria-label="Scroll left"
                        >
                          <i className="fas fa-chevron-left" aria-hidden="true"></i>
                        </button>
                        <button
                          className="event-details-page__nav-arrow event-details-page__nav-arrow--right"
                          onClick={scrollSchedulesRight}
                          aria-label="Scroll right"
                        >
                          <i className="fas fa-chevron-right" aria-hidden="true"></i>
                        </button>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            <aside className="event-details-page__sidebar">
              <div className="event-details-page__section-heading related-event related-events-heading">
                <h2>Related Events</h2>
              </div>
              <div className="event-details-page__related-events-list">
                {loadingRelatedEvents ? (
                  <p>Loading related events...</p>
                ) : relatedEvents.length > 0 ? (
                  relatedEvents.map((event, index) => {
                    const handleViewDetails = () => {
                      if (event.eventId) {
                        history.push(`/event_details/${event.eventId}`);
                      } else {
                        toast.error('Unable to navigate to event details');
                      }
                    };

                    return (
                      <RelatedEventCard
                        key={event.eventId || index}
                        title={event.title}
                        date={event.date}
                        location={event.location || 'Lonavala'}
                        language={event.language}
                        ctaLabel="View Details"
                        onViewDetails={handleViewDetails}
                        onShare={() => handleShare({ event_id: event.eventId, title: event.title })}
                      />
                    );
                  })
                ) : (
                  // Fallback to schedules if no related events found
                  schedules.slice(0, 3).map((schedule, index) => (
                    <RelatedEventCard
                      key={schedule.schedule_id || index}
                      title={eventInfo?.title || schedule.title}
                      date={schedule.start_date_format?.split(',')[1]?.trim().split(' ')[0] + ' ' + schedule.start_date_format?.split(',')[1]?.trim().split(' ')[1]}
                      location={schedule.city_name || 'Lonavala'}
                      language={schedule.language || schedule.language_name || eventInfo?.arr_event_languages?.[0]?.label}
                      ctaLabel="View Details"
                      onViewDetails={() => handleBookNow(schedule)}
                      onShare={() => handleShare(schedule)}
                    />
                  ))
                )}
                {!loadingRelatedEvents && relatedEvents.length === 0 && schedules.length === 0 && (
                  <p className="event-details-page__empty">No related events available.</p>
                )}
              </div>
            </aside>
          </div>
        </section>

        {videos.length > 0 && (
          <section className="event-details-page__section">
            <div className="event-details-page__section-heading">
              <img 
                src={videoIcon}
                alt=""
                className="event-details-page__video-icon"
                aria-hidden="true"
              />
              <h2>Related Videos</h2>
            </div>
            <div className="event-details-page__video-grid">
              {videos.map((video, index) => (
                <VideoTestimonialCard
                  key={video.videoId || index}
                  thumbnail={video.thumbnail}
                  title={parseDraftJsToText(video.title) || 'Event Video'}
                  onPlay={() => openVideoLightbox(index, videos, false)}
                />
              ))}
            </div>
          </section>
        )}



        <section className="event-details-page__section event-details-page__section--about-bg">
          <CourseInfoSection
            title={aboutSectionTitle}
            content={aboutText || introText}
            className="course-info-section--with-bg"
          />
        </section>

        {introText && (
          <CourseInfoSection
            title="Introduction"
            content={introText}
            className="course-info-section--introduction"
          />
        )}

        {/* <EventTabsSection
          tabs={[
            {
              id: 'why-attend',
              label: 'Why attend',
              content: loadingWhyAttend 
                ? '<p>Loading...</p>' 
                : whyAttendItems.length > 0 
                  ? formatWhyAttendAsHTML(whyAttendItems)
                  : (eventInfo?.why_attend || eventInfo?.benefits || '<p>Content will be available soon.</p>'),
            },
            {
              id: 'instructions',
              label: 'Instructions',
              content: loadingInstructions 
                ? '<p>Loading...</p>' 
                : instructionItems.length > 0 
                  ? formatInstructionsAsHTML(instructionItems)
                  : (eventInfo?.instructions || eventInfo?.guidelines || '<p>Content will be available soon.</p>'),
            },
            {
              id: 'venue',
              label: 'How to reach venue',
              content: loadingVenues 
                ? '<p>Loading...</p>' 
                : venueItems.length > 0 
                  ? formatVenuesAsHTML(venueItems)
                  : (eventInfo?.venue_directions || eventInfo?.how_to_reach || '<p>Content will be available soon.</p>'),
            },
            {
              id: 'stay',
              label: 'Stay Facilities',
              content: loadingStayFacilities 
                ? '<p>Loading...</p>' 
                : stayFacilities.length > 0 
                  ? formatStayFacilitiesAsHTML(stayFacilities)
                  : (eventInfo?.stay_facilities || eventInfo?.accommodation || '<p>Content will be available soon.</p>'),
            },
            {
              id: 'topics',
              label: 'Topics',
              content: (loadingSubtopics || loadingFilterOptions)
                ? '<p>Loading...</p>' 
                : (eventSubtopics.length > 0
                  ? `<p style="margin:8px 0;color:#4a4a4a;">${eventSubtopics.map(st => {
                      const name = (st.name || st);
                      return name.replace(/</g,'&lt;').replace(/>/g,'&gt;');
                    }).join(' &nbsp;|&nbsp; ')}</p>`
                  : (filterOptions.topics.length > 0
                      ? formatTopicsAsHTML(filterOptions.topics, eventInfo)
                      : (eventInfo?.topics || eventInfo?.curriculum || '<p>Content will be available soon.</p>'))),
            },
            {
              id: 'schedule',
              label: 'Schedule',
              content: loading 
                ? '<p>Loading...</p>' 
                : schedules.length > 0 
                  ? formatSchedulesAsHTML(schedules)
                  : (eventInfo?.schedule_details || eventInfo?.program_schedule || '<p>Content will be available soon.</p>'),
            },
            {
              id: 'faq',
              label: 'FAQ',
              content: loadingFaqs 
                ? '<p>Loading...</p>' 
                : faqItems.length > 0 
                  ? formatFAQsAsHTML(faqItems)
                  : (eventInfo?.faq || eventInfo?.frequently_asked_questions || '<p>Content will be available soon.</p>'),
            },
            {
              id: 'related-events',
              label: 'Related Events',
              content: loadingRelatedEvents 
                ? '<p>Loading...</p>' 
                : relatedEvents.length > 0 
                  ? formatRelatedEventsAsHTML(relatedEvents)
                  : (eventInfo?.related_events_info || '<p>Content will be available soon.</p>'),
            },
          ]}
        /> */}

        <EventTabsSection key={eventId} tabs={tabsToShow} />


        <TestimonialsSection
          videos={videoTestimonials.map((testimonial, index) => ({
            video_id: testimonial.video_id,
            thumbnail: testimonial.thumbnail,
            title: testimonial.title,
            location: '', // Not provided by testimonials API
            likes: 0, // Not provided by testimonials API
            dislikes: 0, // Not provided by testimonials API
            onPlay: () => openVideoLightbox(index, videoTestimonials, true),
          }))}
          reviews={reviews}
          eventId={eventId}
          userId={localStorage.getItem('user_id') || 0}
          onReviewSubmitted={fetchReviews}
        />


        {isValidWhatsAppLink(whatsappCommunityLink) && (
          <CommunityCallToAction
            title="Join Our Manashakti Group"
            description="Connect with like-minded parents, share experiences, and get support throughout your prenatal journey. Join our WhatsApp Group for daily tips, guidance, and inspiration."
            buttonText="Join WhatsApp Group"
            whatsappLink={whatsappCommunityLink}
          />
        )}

        <RelatedProductsSection
          products={products}
          onAddToCart={handleAddProductToCart}
        />

        <PastEventsGallery
          images={galleryImages}
          onImageClick={(index) => {
            // Open lightbox or modal with image
          }}
        />

        {/* Event Subtopics rectangle chip - now clickable */}
        {eventSubtopics.length > 0 && (
          <section className="event-details-page__section event-details-page__subtopics">
            <div className="event-details-page__subtopics-grid">
              {eventSubtopics.map((subtopicObj, index) => {
                const topicId = eventInfo?.topicId || eventInfo?.topic_id || passedTopicId || 
                  (Array.isArray(eventInfo?.topics) && eventInfo.topics.length > 0 
                    ? (eventInfo.topics[0].id || eventInfo.topics[0].topic_id) 
                    : '');
                const subtopicName = subtopicObj.name || subtopicObj;
                const subtopicId = subtopicObj.id || '';
                const subtopicUrl = `/topics/${encodeURIComponent(subtopicName)}/${topicId}/${subtopicId}`;
                
                return (
                  <Link 
                    key={index} 
                    to={subtopicUrl}
                    className="event-details-page__subtopic-chip event-details-page__subtopic-chip--clickable"
                  >
                    {subtopicName}
                  </Link>
                );
              })}
            </div>
          </section>
        )}

      </main>

      <Footer share_image_url={eventInfo?.share_image_url} description={eventInfo?.social_description} />
      
      {/* Video Lightbox */}
      {lightboxOpen && (
        <div 
          id="openGallery" 
          className="openGallerydiv" 
          style={{ 
            display: 'flex', 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            width: '100%', 
            height: '100%', 
            zIndex: 9999,
            backgroundColor: 'rgba(0, 0, 0, 0.9)'
          }}
        >
          <ReactImageVideoLightbox
            data={lightboxVideoData}
            startIndex={lightboxStartIndex}
            showResourceCount={true}
            onCloseCallback={closeVideoLightbox}
          />
        </div>
      )}

      {/* Appointment Alert Modal for Machine Tests */}
      <div className="modal fade z-index--99999" id="MachineTestModal">
        <div className="modal-dialog modal-dialog-scrollable modal-dialog-centered modal-xl" role="document">
          <div className="modal-content bg-light-light">
            <div className="modal-header" style={{ background: '#9d0b0e', height: '15%' }}>
              <h5 className="modal-title" id="exampleModalLabel" style={{ marginTop: '-5px', color: 'white', fontSize: '15px' }}>
                {t('home.APPOINTMENT ALERT')}
              </h5>
              <span style={{ color: 'white', cursor: 'pointer', marginTop: '-10px' }} data-dismiss="modal" aria-label="Close">X</span>
            </div>
            <div className="modal-body">
              <div className="row">
                <div className="col-xl-12 col-lg-12 col-md-12 col-sm-12 col-xs-12">
                  &nbsp;
                  <img src={getLanguage() === 'ma' ? MachineTests_mr : MachineTests} height={77} className="img-fluid d-xl-none d-lg-none d-md-none d-block" />
                  <img height="36" width="36" className="img-fluid d-xl-inline d-lg-inline d-md-inline d-none" src={thumbImage} />
                  &nbsp; {t('home.thank you for choosing Machine Tests')}
                  <br />
                  <p style={{ marginTop: '13px' }}>
                    {t('home.research_center')}
                    <br />
                    {t('home.avoid inconvenience to you')}
                  </p>
                  <p>
                    {t('home.If you have taken the appointment please')}
                  </p>
                </div>
              </div>
              <div className="row">
                <div className="col-xl-12 col-lg-12 col-md-12 d-xl-flex d-lg-flex d-md-flex d-none">
                  <button type="button" className="btn btn-brown btn-sm mb-2" onClick={addToCartContinue}>
                    {t('home.CONTINUE Machine Test')}
                  </button>
                </div>
                <div className="col-xl-12 col-lg-12 col-md-12 d-xl-flex d-lg-flex d-md-flex d-none">
                  {t('home.call_on_machine')}
                </div>
                <div className="col-xl-12 col-lg-12 col-md-12 d-xl-flex d-lg-flex d-md-flex d-none">
                  <button style={{ color: 'white', cursor: 'pointer' }} className="btn btn-brown btn-sm mb-2" data-dismiss="modal" aria-label="Close">
                    {t('home.CLOSE Machine Test')}
                  </button>
                </div>

                <div className="col-sm-12 col-xs-12 d-xl-none d-lg-none d-md-none d-flex">
                  <button type="button" className="btn btn-brown btn-sm mb-2 btn-block" onClick={addToCartContinue}>
                    {t('home.CONTINUE Machine Test')}
                  </button>
                </div>
                <div className="col-sm-12 col-xs-12 d-xl-none d-lg-none d-md-none d-flex">
                  {t('home.call_on_machine')}
                </div>
                <div className="col-sm-12 col-xs-12 d-xl-none d-lg-none d-md-none d-flex">
                  <button className="btn btn-outline-brown btn-sm mb-2 btn-block" data-dismiss="modal" aria-label="Close">
                    {t('home.CLOSE Machine Test')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default translate(EventDetailsPage);

